import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import type {
  AdvanceExpenseCategory,
  AdvancePurposeCategory,
  ClearEmployeeAdvance,
  CreateAdvanceClearanceLine,
  CreateEmployeeAdvance,
  EmployeeAdvanceListRow,
  PerDiemRate,
} from '@dcc-erp/shared';
import { CreateEmployeeAdvanceSchema, fmt, fmtDate } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { AdvanceApprovalPanel } from '../components/advance/AdvanceApprovalPanel';
import { DisburseModal } from '../components/advance/DisburseModal';
import { WriteOffModal, type WriteOffPayload } from '../components/advance/WriteOffModal';
import { BranchSelect, DepartmentSelect } from '../components/dimensions';
import { HelpButton } from '../components/help/HelpButton';
import { PageHead } from '../components/layout';
import { SearchSelect } from '../components/SearchSelect';
import {
  SpreadsheetImportModal,
  type ImportFieldSpec,
  type ImportResult,
  type ParsedUnit,
  type PreviewColumn,
} from '../components/SpreadsheetImportModal';
import {
  useActiveCompany,
  useActiveCompanyId,
  useAdvanceExpenseCategories,
  useAdvancePrecheck,
  useAdvances,
  useBranches,
  useBulkRemind,
  useClearAdvance,
  useClearPayrollDeduction,
  useCoa,
  useCreateAdvance,
  useDepartments,
  useDisburseAdvance,
  useEmployees,
  usePayrollDeductions,
  usePerDiemRates,
  usePerDiemTaxable,
  useRemindAdvance,
  useSubmitAdvance,
  useUploadAdvanceReceipt,
  useWriteOffAdvance,
} from '../lib/queries';
import { printAdvanceVoucher } from '../lib/queries/advances';
import { exportRows, headerIndexer, todayStamp, type ExportColumn } from '../lib/xlsx';

// =============================================================================
// Employee Advance route — wired to /companies/:cid/advances via TanStack Query.
// The prototype data shape (status: open/overdue/...) is normalised to a single
// derived shape via mapApiRow() so the rich DOM from the prototype stays intact.
// =============================================================================

type AdvTab = 'all' | 'requested' | 'approved' | 'open' | 'overdue' | 'cleared';

interface AdvPurpose {
  code: AdvancePurposeCategory;
  label: string;
  tone: string;
}

export interface Advance {
  id: string;
  voucherNo: string;
  apiId: string | null;
  date: string;
  emp: string;
  dept: string;
  departmentId: string | null;
  purpose: AdvancePurposeCategory;
  desc: string;
  requested: number;
  approved: number;
  paid: number;
  dueClear: string;
  cleared: string | null;
  cleared_amt: number;
  returned: number;
  owed: number;
  status: 'requested' | 'approved' | 'open' | 'overdue' | 'cleared';
  payMethod: 'transfer' | 'cash';
  approvalStatus: string | null;
  currentStep: number;
}

interface BreakdownItem {
  label: string;
  amt: number;
}

interface AgingBucket {
  label: string;
  amt: number;
  n: number;
  color: string;
}

const SEARCH_BG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%238a8a93' stroke-width='2'%3E%3Ccircle cx='11' cy='11' r='7'/%3E%3Cpath d='M21 21l-4.3-4.3'/%3E%3C/svg%3E\")";

const ADV_PURPOSES: readonly AdvPurpose[] = [
  { code: 'TRAVEL_LOCAL', label: 'เดินทางในประเทศ', tone: '#1a56a6' },
  { code: 'TRAVEL_INTL', label: 'เดินทางต่างประเทศ', tone: '#7030A0' },
  { code: 'SITE_VISIT', label: 'ออกหน้างาน / ติดตั้งสินค้า', tone: '#b54708' },
  { code: 'CLIENT_ENT', label: 'รับรองลูกค้า', tone: '#0e7490' },
  { code: 'TRAINING', label: 'อบรม / สัมมนา', tone: '#117a3d' },
  { code: 'EXHIBITION', label: 'งานแสดงสินค้า', tone: '#a16207' },
  { code: 'PURCHASE', label: 'จัดซื้อพิเศษ', tone: '#b42318' },
  { code: 'OTHER', label: 'อื่นๆ', tone: '#52525b' },
];

// ---- Spreadsheet import/export (นำเข้า / ส่งออก) --------------------------
// One spreadsheet row = one EmployeeAdvance create. Header keys match the
// exporter so an exported file round-trips straight back through นำเข้า.
const ADV_IMPORT_FIELDS: ImportFieldSpec[] = [
  {
    col: 'advanceDate',
    label: 'วันที่ขอเบิก',
    required: true,
    hint: 'รูปแบบ YYYY-MM-DD เช่น 2026-05-29',
  },
  {
    col: 'employeeCode',
    label: 'รหัสพนักงาน',
    required: true,
    hint: 'รหัสพนักงานในระบบ (ต้องมีอยู่จริง)',
  },
  { col: 'purpose', label: 'รายละเอียดภารกิจ', required: true, hint: 'คำอธิบายการเบิก' },
  {
    col: 'purposeCategory',
    label: 'ประเภทวัตถุประสงค์',
    required: true,
    hint: 'TRAVEL_LOCAL, TRAVEL_INTL, SITE_VISIT, CLIENT_ENT, TRAINING, EXHIBITION, PURCHASE, OTHER',
  },
  {
    col: 'branchCode',
    label: 'รหัสสาขา',
    required: false,
    hint: 'เว้นว่างได้ — ถ้าระบุ ต้องมีอยู่จริง',
  },
  {
    col: 'departmentCode',
    label: 'รหัสหน่วยงาน',
    required: false,
    hint: 'เว้นว่างได้ — ถ้าระบุ ต้องมีอยู่จริง',
  },
  { col: 'amount', label: 'จำนวนเงิน', required: true, hint: 'ตัวเลข > 0' },
  {
    col: 'clearDeadlineDate',
    label: 'กำหนดเคลียร์',
    required: true,
    hint: 'รูปแบบ YYYY-MM-DD เช่น 2026-06-05',
  },
];

const ADV_TEMPLATE_COLUMNS: string[] = [
  'advanceDate',
  'employeeCode',
  'purpose',
  'purposeCategory',
  'branchCode',
  'departmentCode',
  'amount',
  'clearDeadlineDate',
];

const ADV_TEMPLATE_EXAMPLES: (string | number | boolean)[][] = [
  [
    '2026-05-29',
    'EMP-001',
    'พบลูกค้าโครงการ MRT — 2 วัน',
    'TRAVEL_LOCAL',
    '',
    '',
    20000,
    '2026-06-05',
  ],
];

const ADV_PREVIEW_COLUMNS: PreviewColumn<CreateEmployeeAdvance>[] = [
  { header: 'วันที่ขอ', width: 100, render: (d) => fmtDate(d.advanceDate) },
  {
    header: 'วัตถุประสงค์',
    render: (d) => getPurpose(d.purposeCategory).label,
  },
  { header: 'รายละเอียด', render: (d) => d.purpose },
  { header: 'จำนวน', width: 110, render: (d) => fmt(d.amount) },
  { header: 'กำหนดเคลียร์', width: 110, render: (d) => fmtDate(d.clearDeadlineDate) },
];

const OTHER_PURPOSE: AdvPurpose = ADV_PURPOSES[7]!;

function getPurpose(code: string): AdvPurpose {
  return ADV_PURPOSES.find((p) => p.code === code) ?? OTHER_PURPOSE;
}

// As-of reference used for ageing math. Today (computed at module load) so the
// page reflects current overdue / aging on each fresh render.
function asOfToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysFrom(date1: string, date2 = asOfToday()): number {
  const d1 = new Date(date1).getTime();
  const d2 = new Date(date2).getTime();
  return Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
}

// Period quick-presets for the งวด selector. Returns inclusive ISO date bounds
// (or null = unbounded). 'ytd' runs Jan-1 → asOf (today by default). Unknown /
// 'all' = no bounds. Pure + deterministic (asOf injectable) for testing.
export function periodToRange(
  period: string,
  asOf: string = asOfToday(),
): { dateFrom: string | null; dateTo: string | null } {
  switch (period) {
    case 'may26':
      return { dateFrom: '2026-05-01', dateTo: '2026-05-31' };
    case 'apr26':
      return { dateFrom: '2026-04-01', dateTo: '2026-04-30' };
    case 'ytd':
      return { dateFrom: '2026-01-01', dateTo: asOf };
    default:
      return { dateFrom: null, dateTo: null };
  }
}

const ADV_TABS: readonly (readonly [AdvTab, string])[] = [
  ['all', 'ทั้งหมด'],
  ['requested', 'คำขอรออนุมัติ'],
  ['approved', 'อนุมัติแล้ว รอจ่าย'],
  ['open', 'จ่ายแล้ว รอเคลียร์'],
  ['overdue', 'เกินกำหนดเคลียร์'],
  ['cleared', 'เคลียร์แล้ว'],
];

// ============================================================
// API row -> prototype Advance shape adapter.
// The API only models OUTSTANDING / PARTIALLY_CLEARED / CLEARED / WRITTEN_OFF —
// the prototype split (requested / approved / open / overdue / cleared) collapses
// onto these via:
//   OUTSTANDING + overdue=true            -> 'overdue'
//   OUTSTANDING                           -> 'open'
//   PARTIALLY_CLEARED + overdue=true      -> 'overdue'
//   PARTIALLY_CLEARED                     -> 'open'
//   CLEARED                               -> 'cleared'
//   WRITTEN_OFF                           -> 'cleared' (treat as final)
// `requested` / `approved` are non-API states (no approval workflow yet) — kept
// in the tabs so the UI still slots into the prototype layout when Phase 4
// adds the approval workflow.
// ============================================================
export function mapApiRow(r: EmployeeAdvanceListRow): Advance {
  const amt = Number(r.amount);
  const cleared = Number(r.totalClearedAmount) + Number(r.refundAmount);
  let status: Advance['status'];
  if (r.status === 'CLEARED' || r.status === 'WRITTEN_OFF') status = 'cleared';
  else if (r.approvalStatus === 'APPROVED' && r.status === 'UNISSUED') status = 'approved';
  else if (r.approvalStatus && r.approvalStatus !== 'APPROVED' && r.status === 'UNISSUED')
    status = 'requested'; // DRAFT / PENDING / RETURNED / REJECTED — pre-disburse
  else if (r.overdue) status = 'overdue';
  else status = 'open';

  return {
    id: r.voucherNo,
    voucherNo: r.voucherNo,
    apiId: r.id,
    date: new Date(r.advanceDate).toISOString().slice(0, 10),
    emp: r.employeeName,
    dept: r.employeeDept ?? '',
    departmentId: r.departmentId ?? null,
    purpose: r.purposeCategory,
    desc: r.purpose,
    requested: amt,
    approved: amt,
    paid: amt,
    dueClear: new Date(r.clearDeadlineDate).toISOString().slice(0, 10),
    cleared: r.status === 'CLEARED' ? new Date(r.updatedAt).toISOString().slice(0, 10) : null,
    cleared_amt: cleared,
    returned: Number(r.refundAmount),
    owed: 0,
    status,
    payMethod: 'transfer',
    approvalStatus: r.approvalStatus ?? null,
    currentStep: r.currentStep ?? 0,
  };
}

export interface AdvanceFilterArgs {
  tab: AdvTab;
  q: string;
  purpose: string;
  dateFrom: string;
  dateTo: string;
  departmentId: string;
}

// Pure table filter — tab/status, purpose, free-text (id/emp/desc), inclusive
// ISO date range on the request date, and department. KPI/aging cards are
// intentionally NOT filtered (point-in-time), only the listing.
export function filterAdvances(rows: readonly Advance[], f: AdvanceFilterArgs): Advance[] {
  const q = f.q.trim().toLowerCase();
  return rows.filter((a) => {
    if (f.tab !== 'all' && a.status !== f.tab) return false;
    if (f.purpose !== 'all' && a.purpose !== f.purpose) return false;
    if (f.departmentId && a.departmentId !== f.departmentId) return false;
    if (f.dateFrom && a.date < f.dateFrom) return false;
    if (f.dateTo && a.date > f.dateTo) return false;
    if (
      q &&
      !(
        a.id.toLowerCase().includes(q) ||
        a.emp.toLowerCase().includes(q) ||
        a.desc.toLowerCase().includes(q)
      )
    )
      return false;
    return true;
  });
}

// Router-free per-row engine actions: requested → "พิจารณา" (open the approval
// panel), approved → "จ่ายเงิน" (open the disburse modal). Other lanes render
// nothing here (the route keeps เคลียร์/ตัดจำหน่าย/ดูสรุป inline). Extracted so the
// lane→action branching is unit-testable without a router (mirrors ApBillRowActions).
export function AdvanceRowActions({
  status,
  onApprove,
  onDisburse,
}: {
  status: Advance['status'];
  onApprove: () => void;
  onDisburse: () => void;
}) {
  if (status === 'requested') {
    return (
      <button className="btn sm primary" style={{ height: 28, fontSize: 14 }} onClick={onApprove}>
        พิจารณา
      </button>
    );
  }
  if (status === 'approved') {
    return (
      <button className="btn sm primary" style={{ height: 28, fontSize: 14 }} onClick={onDisburse}>
        จ่ายเงิน →
      </button>
    );
  }
  return null;
}

export const Route = createFileRoute('/advance')({
  component: AdvancePage,
});

export function AdvancePage() {
  const companyId = useActiveCompanyId();
  const company = useActiveCompany();
  const advancesQuery = useAdvances(companyId);
  const { data: employees = [] } = useEmployees(companyId);
  const { data: branches = [] } = useBranches(companyId);
  const { data: departments = [] } = useDepartments(companyId);
  const createAdvance = useCreateAdvance(companyId);

  const rawRows: readonly EmployeeAdvanceListRow[] = advancesQuery.data ?? [];

  const ADV_DATA: readonly Advance[] = useMemo(
    () => (advancesQuery.data ?? []).map(mapApiRow),
    [advancesQuery.data],
  );

  const [tab, setTab] = useState<AdvTab>('all');
  const [q, setQ] = useState('');
  const [purposeF, setPurposeF] = useState('all');
  const [periodValue, setPeriodValue] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [deptFilter, setDeptFilter] = useState('');

  function onPeriodChange(v: string) {
    setPeriodValue(v);
    const { dateFrom: f, dateTo: t } = periodToRange(v);
    setDateFrom(f ?? '');
    setDateTo(t ?? '');
  }
  const [showRequest, setShowRequest] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [clearTarget, setClearTarget] = useState<Advance | null>(null);
  const [writeOffTarget, setWriteOffTarget] = useState<Advance | null>(null);
  const writeOffAdvance = useWriteOffAdvance(companyId);
  const [approvalTargetId, setApprovalTargetId] = useState<string | null>(null);
  const [disburseTargetId, setDisburseTargetId] = useState<string | null>(null);
  const disburseAdvance = useDisburseAdvance(companyId);
  // M4 — manual overdue remind ("ส่งเตือนทาง Email") + voucher print ("พิมพ์รายงาน").
  const remindAdvance = useRemindAdvance(companyId);
  const [actionNote, setActionNote] = useState<string | null>(null);
  // M5 — functional bulk row-select (a real Set of apiIds) + bulk overdue remind.
  const bulkRemind = useBulkRemind(companyId);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const toggleSelect = (apiId: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(apiId)) next.delete(apiId);
      else next.add(apiId);
      return next;
    });
  const approvalRow = approvalTargetId
    ? (rawRows.find((r) => r.id === approvalTargetId) ?? null)
    : null;
  const disburseRow = disburseTargetId
    ? (rawRows.find((r) => r.id === disburseTargetId) ?? null)
    : null;

  // Code → id maps for import; id → code maps for export.
  const employeeByCode = useMemo(
    () => new Map(employees.map((e) => [e.code.toLowerCase(), e.id])),
    [employees],
  );
  const employeeCodeById = useMemo(
    () => new Map(employees.map((e) => [e.id, e.code])),
    [employees],
  );
  const branchByCode = useMemo(
    () => new Map(branches.map((b) => [b.code.toLowerCase(), b.id])),
    [branches],
  );
  const departmentByCode = useMemo(
    () => new Map(departments.map((d) => [d.code.toLowerCase(), d.id])),
    [departments],
  );

  const EXPORT_COLS: ExportColumn<EmployeeAdvanceListRow>[] = [
    { header: 'voucherNo', value: (r) => r.voucherNo, width: 16 },
    { header: 'advanceDate', value: (r) => String(r.advanceDate).slice(0, 10), width: 12 },
    {
      header: 'employeeCode',
      value: (r) => employeeCodeById.get(r.employeeId) ?? '',
      width: 14,
    },
    { header: 'purpose', value: (r) => r.purpose, width: 32 },
    { header: 'purposeCategory', value: (r) => r.purposeCategory, width: 16 },
    { header: 'amount', value: (r) => Number(r.amount), width: 14 },
    {
      header: 'clearDeadlineDate',
      value: (r) => String(r.clearDeadlineDate).slice(0, 10),
      width: 14,
    },
    { header: 'totalClearedAmount', value: (r) => Number(r.totalClearedAmount), width: 16 },
    { header: 'outstandingAmount', value: (r) => Number(r.outstandingAmount), width: 16 },
    { header: 'status', value: (r) => r.status, width: 16 },
  ];

  async function handleExport() {
    await exportRows(
      `เงินทดรองจ่าย_${company?.code ?? 'COMPANY'}_${todayStamp()}.xlsx`,
      'เงินทดรองจ่าย',
      EXPORT_COLS,
      rawRows,
    );
  }

  function parseAdvances(grid: string[][]): {
    units: ParsedUnit<CreateEmployeeAdvance>[];
    headerError: string | null;
  } {
    const header = grid[0];
    if (!header) return { units: [], headerError: 'ไฟล์ว่างเปล่า' };
    const { idx, cell } = headerIndexer(header);
    for (const req of [
      'advanceDate',
      'employeeCode',
      'purpose',
      'purposeCategory',
      'amount',
      'clearDeadlineDate',
    ]) {
      if (idx(req) === -1) return { units: [], headerError: `ไม่พบคอลัมน์ที่จำเป็น: ${req}` };
    }
    const units: ParsedUnit<CreateEmployeeAdvance>[] = [];
    for (let r = 1; r < grid.length; r++) {
      const cols = grid[r];
      if (!cols || cols.every((c) => c.trim() === '')) continue;
      const rowNo = r + 1;

      const employeeCode = cell(cols, 'employeeCode');
      const employeeId = employeeCode ? employeeByCode.get(employeeCode.toLowerCase()) : undefined;
      if (!employeeId) {
        units.push({ rowNo, data: null, error: `ไม่พบพนักงาน: ${employeeCode}` });
        continue;
      }

      const branchCode = cell(cols, 'branchCode');
      const branchId = branchCode ? branchByCode.get(branchCode.toLowerCase()) : undefined;
      if (branchCode && !branchId) {
        units.push({ rowNo, data: null, error: `ไม่พบสาขา: ${branchCode}` });
        continue;
      }

      const departmentCode = cell(cols, 'departmentCode');
      const departmentId = departmentCode
        ? departmentByCode.get(departmentCode.toLowerCase())
        : undefined;
      if (departmentCode && !departmentId) {
        units.push({ rowNo, data: null, error: `ไม่พบหน่วยงาน: ${departmentCode}` });
        continue;
      }

      const candidate = {
        advanceDate: cell(cols, 'advanceDate'),
        employeeId,
        purpose: cell(cols, 'purpose'),
        purposeCategory: cell(cols, 'purposeCategory'),
        amount: Number(cell(cols, 'amount')),
        clearDeadlineDate: cell(cols, 'clearDeadlineDate'),
        ...(branchId ? { branchId } : {}),
        ...(departmentId ? { departmentId } : {}),
      };
      const parsed = CreateEmployeeAdvanceSchema.safeParse(candidate);
      units.push(
        parsed.success
          ? { rowNo, data: parsed.data, error: null }
          : { rowNo, data: null, error: parsed.error.issues[0]?.message ?? 'ข้อมูลไม่ถูกต้อง' },
      );
    }
    return { units, headerError: null };
  }

  async function runImport(
    valid: ParsedUnit<CreateEmployeeAdvance>[],
    onProgress: (done: number, total: number) => void,
  ): Promise<ImportResult> {
    const res: ImportResult = { created: 0, updated: 0, failed: 0, errors: [] };
    for (let i = 0; i < valid.length; i++) {
      const u = valid[i];
      if (!u?.data) continue;
      try {
        await createAdvance.mutateAsync({
          data: u.data,
          idempotencyKey: `adv-create-${crypto.randomUUID()}`,
        });
        res.created++;
      } catch (e) {
        res.failed++;
        res.errors.push({
          code: `แถว ${u.rowNo}`,
          message: e instanceof Error ? e.message : 'เกิดข้อผิดพลาด',
        });
      }
      onProgress(i + 1, valid.length);
    }
    return res;
  }

  const filtered = useMemo(
    () =>
      filterAdvances(ADV_DATA, {
        tab,
        q,
        purpose: purposeF,
        dateFrom,
        dateTo,
        departmentId: deptFilter,
      }),
    [ADV_DATA, tab, q, purposeF, dateFrom, dateTo, deptFilter],
  );

  // M4 — footer-action targets. Print = the first listed (issued) advance's voucher.
  // Remind = the first OVERDUE advance only (the natural dunning target). We do NOT
  // fall back to any open advance: emailing a not-yet-due employee is a wrong-target
  // hazard, so the button stays disabled when nothing is overdue.
  const printableAdvance = useMemo(() => filtered.find((a) => a.apiId), [filtered]);
  const remindTarget = useMemo(
    () => filtered.find((a) => a.status === 'overdue' && a.apiId),
    [filtered],
  );

  // M5 — bulk selection over the currently-filtered rows. Only issued rows (apiId)
  // are selectable. The selected set may outlive a filter change; we intersect with
  // the visible ids when acting / rendering the header state.
  const selectableIds = useMemo(
    () => filtered.map((a) => a.apiId).filter((id): id is string => !!id),
    [filtered],
  );
  const visibleSelected = useMemo(
    () => selectableIds.filter((id) => selectedIds.has(id)),
    [selectableIds, selectedIds],
  );
  const allVisibleSelected =
    selectableIds.length > 0 && visibleSelected.length === selectableIds.length;
  const toggleSelectAll = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selectableIds.every((id) => next.has(id))) {
        selectableIds.forEach((id) => next.delete(id));
      } else {
        selectableIds.forEach((id) => next.add(id));
      }
      return next;
    });
  const runBulkRemind = () => {
    const ids = visibleSelected;
    if (ids.length === 0 || !companyId) return;
    if (!window.confirm(`ส่งอีเมลเตือน ${ids.length} รายการที่เลือก?`)) return;
    bulkRemind.mutate(
      { ids },
      {
        onSuccess: (rows) => {
          const sent = rows.filter((r) => r.sent).length;
          const failed = rows.length - sent;
          setActionNote(
            `ส่งเตือนแล้ว ${sent} รายการ${failed ? ` · ข้าม ${failed} (ไม่เกินกำหนด/ปิดแล้ว)` : ''}`,
          );
          setSelectedIds(new Set());
        },
        onError: (e) => setActionNote(`ส่งเตือนไม่สำเร็จ: ${e instanceof Error ? e.message : ''}`),
      },
    );
  };

  const totals = useMemo(() => {
    const open = ADV_DATA.filter((a) => a.status === 'open' || a.status === 'overdue');
    return {
      outstanding: open.reduce((a, x) => a + x.paid - x.cleared_amt, 0),
      requestPending: ADV_DATA.filter((a) => a.status === 'requested').reduce(
        (a, x) => a + x.requested,
        0,
      ),
      requestCount: ADV_DATA.filter((a) => a.status === 'requested').length,
      overdueCount: ADV_DATA.filter((a) => a.status === 'overdue').length,
      overdueAmt: ADV_DATA.filter((a) => a.status === 'overdue').reduce((a, x) => a + x.paid, 0),
      clearedYTD: ADV_DATA.filter((a) => a.status === 'cleared').reduce(
        (a, x) => a + x.cleared_amt,
        0,
      ),
      openCount: open.length,
    };
  }, [ADV_DATA]);

  const aging = useMemo<AgingBucket[]>(() => {
    const open = ADV_DATA.filter((a) => a.status === 'open' || a.status === 'overdue');
    const buckets = { fresh: 0, w1: 0, w2: 0, w4: 0, over: 0 };
    const cnts = { fresh: 0, w1: 0, w2: 0, w4: 0, over: 0 };
    open.forEach((a) => {
      const days = daysFrom(a.date);
      if (days <= 7) {
        buckets.fresh += a.paid;
        cnts.fresh++;
      } else if (days <= 14) {
        buckets.w1 += a.paid;
        cnts.w1++;
      } else if (days <= 21) {
        buckets.w2 += a.paid;
        cnts.w2++;
      } else if (days <= 30) {
        buckets.w4 += a.paid;
        cnts.w4++;
      } else {
        buckets.over += a.paid;
        cnts.over++;
      }
    });
    return [
      { label: '0–7 วัน', amt: buckets.fresh, n: cnts.fresh, color: 'var(--positive)' },
      { label: '8–14 วัน', amt: buckets.w1, n: cnts.w1, color: 'var(--info)' },
      { label: '15–21 วัน', amt: buckets.w2, n: cnts.w2, color: 'var(--warning)' },
      { label: '22–30 วัน', amt: buckets.w4, n: cnts.w4, color: '#c2410c' },
      { label: 'เกิน 30 วัน', amt: buckets.over, n: cnts.over, color: 'var(--negative)' },
    ];
  }, [ADV_DATA]);

  const agingTotal = aging.reduce((s, b) => s + b.amt, 0);

  return (
    <div className="page">
      <PageHead
        crumbs={['ลูกหนี้ / เจ้าหนี้', 'เงินทดรองจ่าย']}
        title="เงินทดรองจ่าย (Employee Advances)"
        actions={
          <>
            <HelpButton guide="advance" />
            <select
              className="select"
              value={periodValue}
              onChange={(e) => onPeriodChange(e.target.value)}
            >
              <option value="all">งวด: ทั้งหมด</option>
              <option value="may26">งวด: พ.ค. 2026</option>
              <option value="apr26">งวด: เม.ย. 2026</option>
              <option value="ytd">YTD 2026</option>
            </select>
            <button className="btn" onClick={() => setShowImport(true)} disabled={!companyId}>
              <Icon name="upload" size={14} /> นำเข้า
            </button>
            <button
              className="btn"
              onClick={() => void handleExport()}
              disabled={rawRows.length === 0}
            >
              <Icon name="download" size={14} /> ส่งออก
            </button>
            <button className="btn primary" onClick={() => setShowRequest(true)}>
              <Icon name="plus" size={14} /> ขอเบิกเงินทดรอง
            </button>
          </>
        }
      />

      <div className="kpi-grid">
        <div className="kpi">
          <div className="label">
            <span className="dot" />
            คงค้างยังไม่เคลียร์
          </div>
          <div className="value tnum">
            {fmt(totals.outstanding)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">
            {totals.openCount} ใบ · ค่าเฉลี่ย{' '}
            {fmt(Math.round(totals.outstanding / Math.max(totals.openCount, 1)))} บาท/ใบ
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--negative)' }} />
            เกินกำหนดเคลียร์
          </div>
          <div
            className="value tnum"
            style={{ color: totals.overdueCount ? 'var(--negative)' : 'inherit' }}
          >
            {totals.overdueCount} <span className="unit">ใบ</span>
          </div>
          <div className="trend down">รวม {fmt(totals.overdueAmt)} บาท · ติดตามด่วน</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--warning)' }} />
            คำขอรออนุมัติ
          </div>
          <div className="value tnum">
            {totals.requestCount} <span className="unit">ใบ</span>
          </div>
          <div className="trend faint">รวม {fmt(totals.requestPending)} บาท</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--positive)' }} />
            เคลียร์แล้วเดือนนี้
          </div>
          <div className="value tnum">
            {fmt(totals.clearedYTD)} <span className="unit">บาท</span>
          </div>
          <div className="trend pos">
            <Icon name="check" size={11} /> {ADV_DATA.filter((a) => a.status === 'cleared').length}{' '}
            ใบ ปิดบัญชีแล้ว
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 'var(--section-gap)' }}>
        <div className="card-head">
          <div>
            <h3>อายุเงินทดรองค้างเคลียร์</h3>
            <div className="sub">
              นโยบายบริษัท: ต้องเคลียร์ภายใน 7 วันหลังกลับจากภารกิจ · เกิน 30 วันต้องชี้แจง ผจก.
              การเงิน
            </div>
          </div>
          <div className="meta">
            รวม {fmt(agingTotal)} บาท · {totals.openCount} ใบ
          </div>
        </div>
        <div className="card-body">
          <div
            style={{
              display: 'flex',
              height: 14,
              borderRadius: 7,
              overflow: 'hidden',
              background: '#f0f0f3',
              marginBottom: 12,
            }}
          >
            {aging.map(
              (b, i) =>
                b.amt > 0 && (
                  <div
                    key={i}
                    style={{
                      width: `${agingTotal ? (b.amt / agingTotal) * 100 : 0}%`,
                      background: b.color,
                    }}
                    title={`${b.label}: ${fmt(b.amt)}`}
                  />
                ),
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14 }}>
            {aging.map((b, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 9, height: 9, borderRadius: 2, background: b.color }} />
                  <span style={{ fontSize: 14, color: 'var(--text-muted)', fontWeight: 500 }}>
                    {b.label}
                  </span>
                </div>
                <div
                  style={{ fontSize: 18, fontWeight: 600, fontFamily: 'var(--font-display)' }}
                  className="tnum"
                >
                  {fmt(b.amt)}
                </div>
                <div className="meta">
                  {b.n} ใบ · {agingTotal ? ((b.amt / agingTotal) * 100).toFixed(1) : 0}%
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="page-tabs">
        {ADV_TABS.map(([k, l]) => {
          const n = k === 'all' ? ADV_DATA.length : ADV_DATA.filter((a) => a.status === k).length;
          return (
            <div key={k} className={`pt ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>
              {l}{' '}
              <span className="faint" style={{ marginLeft: 4 }}>
                ({n})
              </span>
            </div>
          );
        })}
      </div>

      <div className="tbl-shell">
        <div className="filter-row">
          <input
            className="input search w-lg"
            placeholder="ค้นหา เลขที่ใบเบิก / ชื่อพนักงาน / รายละเอียด"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{
              paddingLeft: 32,
              backgroundImage: SEARCH_BG,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: '10px center',
            }}
          />
          <select className="select" value={purposeF} onChange={(e) => setPurposeF(e.target.value)}>
            <option value="all">วัตถุประสงค์: ทั้งหมด</option>
            {ADV_PURPOSES.map((p) => (
              <option key={p.code} value={p.code}>
                {p.label}
              </option>
            ))}
          </select>
          <select
            className="select"
            value={deptFilter}
            onChange={(e) => setDeptFilter(e.target.value)}
          >
            <option value="">แผนก: ทั้งหมด</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.code} — {d.nameTh}
              </option>
            ))}
          </select>
          <select className="select">
            <option>วิธีจ่าย: ทั้งหมด</option>
            <option>โอนธนาคาร</option>
            <option>เงินสด</option>
          </select>
          <input
            className="input w-sm"
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPeriodValue('all');
            }}
          />
          <span className="faint">ถึง</span>
          <input
            className="input w-sm"
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPeriodValue('all');
            }}
          />
        </div>
        <div className="tbl-wrap" style={{ maxHeight: 560 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 24 }}>
                  <input
                    type="checkbox"
                    data-testid="row-select-all"
                    aria-label="เลือกทั้งหมด"
                    checked={allVisibleSelected}
                    disabled={selectableIds.length === 0}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th style={{ width: 130 }}>เลขที่ใบเบิก</th>
                <th style={{ width: 95 }}>วันที่ขอ</th>
                <th>พนักงาน / แผนก</th>
                <th>วัตถุประสงค์</th>
                <th>รายละเอียด</th>
                <th className="num">ยอดขอ</th>
                <th className="num">อนุมัติ</th>
                <th className="num">จ่ายจริง</th>
                <th>กำหนดเคลียร์</th>
                <th>อายุ</th>
                <th>สถานะ</th>
                <th style={{ width: 100 }}>การกระทำ</th>
              </tr>
            </thead>
            <tbody>
              {advancesQuery.isPending ? (
                <tr>
                  <td colSpan={13} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                    กำลังโหลด…
                  </td>
                </tr>
              ) : advancesQuery.error ? (
                <tr>
                  <td
                    colSpan={13}
                    className="muted"
                    style={{ textAlign: 'center', padding: 24, color: 'var(--negative)' }}
                  >
                    โหลดล้มเหลว: {advancesQuery.error.message}
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={13} className="muted" style={{ textAlign: 'center', padding: 32 }}>
                    ยังไม่มีข้อมูล — กดปุ่ม &quot;<b>+ ขอเบิกเงินทดรอง</b>&quot; เพื่อสร้างรายการแรก
                  </td>
                </tr>
              ) : null}
              {filtered.map((a) => {
                const days = daysFrom(a.date);
                const pp = getPurpose(a.purpose);
                return (
                  <tr key={a.id}>
                    <td>
                      <input
                        type="checkbox"
                        data-testid={a.apiId ? `row-select-${a.apiId}` : undefined}
                        aria-label={`เลือก ${a.id}`}
                        disabled={!a.apiId}
                        checked={!!a.apiId && selectedIds.has(a.apiId)}
                        onChange={() => a.apiId && toggleSelect(a.apiId)}
                      />
                    </td>
                    <td className="code">
                      {a.apiId ? (
                        <Link
                          to="/advance/$id"
                          params={{ id: a.apiId }}
                          style={{ color: 'var(--brand)', fontWeight: 500 }}
                        >
                          {a.id}
                        </Link>
                      ) : (
                        a.id
                      )}
                    </td>
                    <td>{fmtDate(a.date)}</td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{a.emp}</div>
                      <div className="meta">{a.dept}</div>
                    </td>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: pp.tone,
                          }}
                        />
                        {pp.label}
                      </span>
                    </td>
                    <td style={{ maxWidth: 320, whiteSpace: 'normal' }}>{a.desc}</td>
                    <td className="num tnum">{fmt(a.requested)}</td>
                    <td className="num tnum">
                      {a.approved ? fmt(a.approved) : <span className="meta">—</span>}
                    </td>
                    <td className="num tnum">
                      <b>{a.paid ? fmt(a.paid) : '—'}</b>
                    </td>
                    <td className={a.status === 'overdue' ? 'neg' : ''}>{fmtDate(a.dueClear)}</td>
                    <td className="num">
                      {a.status === 'cleared' && a.cleared ? (
                        <span className="badge positive" style={{ fontSize: 13 }}>
                          เสร็จใน {daysFrom(a.date, a.cleared)} วัน
                        </span>
                      ) : a.status === 'requested' || a.status === 'approved' ? (
                        <span className="meta">—</span>
                      ) : (
                        <span
                          style={{
                            fontWeight: 600,
                            color:
                              days > daysFrom(a.date, a.dueClear)
                                ? 'var(--negative)'
                                : 'var(--text)',
                          }}
                        >
                          {days} วัน
                        </span>
                      )}
                    </td>
                    <td>
                      {a.status === 'requested' ? (
                        <span className="badge warning">
                          <span className="bdot" />
                          รออนุมัติ
                        </span>
                      ) : a.status === 'approved' ? (
                        <span className="badge info">
                          <span className="bdot" />
                          รอจ่าย
                        </span>
                      ) : a.status === 'open' ? (
                        <span className="badge brand">
                          <span className="bdot" />
                          รอเคลียร์
                        </span>
                      ) : a.status === 'overdue' ? (
                        <span className="badge negative">
                          <span className="bdot" />
                          เกินกำหนด
                        </span>
                      ) : (
                        <span className="badge positive">
                          <span className="bdot" />
                          เคลียร์แล้ว
                        </span>
                      )}
                    </td>
                    <td>
                      {a.apiId && (
                        <AdvanceRowActions
                          status={a.status}
                          onApprove={() => setApprovalTargetId(a.apiId)}
                          onDisburse={() => setDisburseTargetId(a.apiId)}
                        />
                      )}
                      {(a.status === 'open' || a.status === 'overdue') && (
                        <>
                          <button
                            className="btn sm primary"
                            style={{ height: 28, fontSize: 14 }}
                            onClick={() => setClearTarget(a)}
                          >
                            เคลียร์ →
                          </button>
                          <button
                            className="btn sm ghost"
                            style={{ height: 28, fontSize: 14, marginLeft: 6 }}
                            onClick={() => setWriteOffTarget(a)}
                          >
                            ตัดจำหน่าย
                          </button>
                        </>
                      )}
                      {a.status === 'cleared' && a.apiId && (
                        <Link
                          to="/advance/$id"
                          params={{ id: a.apiId }}
                          className="btn sm ghost"
                          style={{ height: 28, fontSize: 14 }}
                        >
                          ดูสรุป
                        </Link>
                      )}
                    </td>
                  </tr>
                );
              })}
              <tr className="total">
                <td colSpan={6}>รวม {filtered.length} ใบ</td>
                <td className="num tnum">{fmt(filtered.reduce((s, a) => s + a.requested, 0))}</td>
                <td className="num tnum">{fmt(filtered.reduce((s, a) => s + a.approved, 0))}</td>
                <td className="num tnum">{fmt(filtered.reduce((s, a) => s + a.paid, 0))}</td>
                <td colSpan={4} />
              </tr>
            </tbody>
          </table>
        </div>
        <div className="tbl-footer">
          <span>
            แสดง {filtered.length} จาก {ADV_DATA.length} ใบ · ลงบัญชี 10110005 — เงินทดรองจ่าย
            (ลูกหนี้พนักงาน)
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {actionNote && (
              <span className="meta" data-testid="advance-action-note">
                {actionNote}
              </span>
            )}
            {visibleSelected.length > 0 && (
              <button
                className="btn sm primary"
                data-testid="bulk-remind-btn"
                disabled={!companyId || bulkRemind.isPending}
                onClick={runBulkRemind}
                title="ส่งอีเมลเตือนรายการที่เลือก"
              >
                <Icon name="bell" size={12} /> ส่งเตือนที่เลือก ({visibleSelected.length})
              </button>
            )}
            <button
              className="btn sm"
              data-testid="print-voucher-btn"
              // Print the disbursement voucher (ใบสำคัญเบิก) of the first listed advance
              // that has been issued (apiId). Authenticated blob download. The target's
              // voucherNo is surfaced in the tooltip so this is not a blind action.
              title={
                printableAdvance
                  ? `พิมพ์ใบสำคัญเบิก ${printableAdvance.voucherNo} (${printableAdvance.emp})`
                  : 'ไม่มีรายการให้พิมพ์'
              }
              disabled={!printableAdvance || !companyId}
              onClick={() => {
                if (!printableAdvance?.apiId) return;
                setActionNote(null);
                void printAdvanceVoucher(companyId, printableAdvance.apiId).catch((e: unknown) =>
                  setActionNote(`พิมพ์ไม่สำเร็จ: ${e instanceof Error ? e.message : ''}`),
                );
              }}
            >
              <Icon name="print" size={12} /> พิมพ์รายงาน
              {printableAdvance && (
                <span className="meta" style={{ marginLeft: 4 }}>
                  ({printableAdvance.voucherNo})
                </span>
              )}
            </button>
            <button
              className="btn sm"
              data-testid="remind-btn"
              // Send an overdue reminder for the first OVERDUE advance (the dunning
              // target). Disabled when nothing is overdue. Irreversible (emails the
              // employee) → confirm and NAME the target before firing.
              title={
                remindTarget
                  ? `ส่งอีเมลเตือน ${remindTarget.voucherNo} → ${remindTarget.emp}`
                  : 'ไม่มีรายการเกินกำหนด'
              }
              disabled={!remindTarget?.apiId || !companyId || remindAdvance.isPending}
              onClick={() => {
                const target = remindTarget;
                if (!target?.apiId) return;
                if (!window.confirm(`ส่งอีเมลเตือน ${target.voucherNo} → ${target.emp}?`)) return;
                remindAdvance.mutate(
                  { id: target.apiId },
                  {
                    onSuccess: (r) => setActionNote(`ส่งเตือนแล้ว → ${r.to.join(', ')}`),
                    onError: (e) =>
                      setActionNote(`ส่งเตือนไม่สำเร็จ: ${e instanceof Error ? e.message : ''}`),
                  },
                );
              }}
            >
              ส่งเตือนทาง Email
            </button>
          </div>
        </div>
      </div>

      <div className="grid-2" style={{ marginTop: 'var(--section-gap)' }}>
        <div className="card">
          <div className="card-head">
            <h3>พนักงานที่มีเงินทดรองค้างเคลียร์</h3>
            <span className="meta">เรียงตามยอดสูงสุด</span>
          </div>
          <div className="card-body flush">
            <table className="tbl">
              <thead>
                <tr>
                  <th>พนักงาน</th>
                  <th>แผนก</th>
                  <th className="num">จำนวนใบ</th>
                  <th className="num">ยอดคงค้าง</th>
                  <th>สถานะ</th>
                </tr>
              </thead>
              <tbody>
                <ByEmployeeRows data={ADV_DATA} />
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h3>เงินทดรองตามวัตถุประสงค์</h3>
            <span className="meta">เดือน พ.ค. 2026</span>
          </div>
          <div className="card-body flush">
            <table className="tbl">
              <thead>
                <tr>
                  <th>วัตถุประสงค์</th>
                  <th className="num">จำนวนใบ</th>
                  <th className="num">ยอดเบิกรวม</th>
                  <th style={{ width: 180 }}>สัดส่วน</th>
                </tr>
              </thead>
              <tbody>
                <ByPurposeRows data={ADV_DATA} />
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 'var(--section-gap)' }}>
        <PayrollDeductionCard companyId={companyId} />
      </div>

      <div style={{ marginTop: 'var(--section-gap)' }}>
        <PerDiemTaxableCard companyId={companyId} />
      </div>

      {showImport && companyId && (
        <SpreadsheetImportModal<CreateEmployeeAdvance>
          title="นำเข้าเงินทดรองจ่าย"
          fields={ADV_IMPORT_FIELDS}
          templateColumns={ADV_TEMPLATE_COLUMNS}
          templateExamples={ADV_TEMPLATE_EXAMPLES}
          templateFilename="employee_advance_template.xlsx"
          sheetName="เงินทดรองจ่าย"
          parse={parseAdvances}
          previewColumns={ADV_PREVIEW_COLUMNS}
          runImport={runImport}
          onClose={() => setShowImport(false)}
        />
      )}
      {showRequest && (
        <AdvanceRequestModal companyId={companyId} onClose={() => setShowRequest(false)} />
      )}
      {clearTarget && (
        <AdvanceClearModal
          companyId={companyId}
          advance={clearTarget}
          onClose={() => setClearTarget(null)}
        />
      )}
      {writeOffTarget?.apiId && (
        <WriteOffModal
          voucherNo={writeOffTarget.id}
          outstanding={writeOffTarget.paid - writeOffTarget.cleared_amt}
          busy={writeOffAdvance.isPending}
          error={writeOffAdvance.isError ? writeOffAdvance.error.message : null}
          onConfirm={(p: WriteOffPayload) => {
            const apiId = writeOffTarget.apiId;
            if (!apiId) return;
            writeOffAdvance.mutate(
              {
                id: apiId,
                data: { writeOffDate: p.writeOffDate, reason: p.reason },
                idempotencyKey: `adv-writeoff-${apiId}-${crypto.randomUUID()}`,
              },
              { onSuccess: () => setWriteOffTarget(null) },
            );
          }}
          onClose={() => setWriteOffTarget(null)}
        />
      )}
      {approvalRow && companyId && (
        <AdvanceApprovalPanel
          companyId={companyId}
          advance={approvalRow}
          onClose={() => setApprovalTargetId(null)}
        />
      )}
      {disburseRow && companyId && (
        <DisburseModal
          voucherNo={disburseRow.voucherNo}
          amount={Number(disburseRow.amount)}
          busy={disburseAdvance.isPending}
          error={disburseAdvance.isError ? disburseAdvance.error.message : null}
          onConfirm={(p) => {
            disburseAdvance.mutate(
              { id: disburseRow.id, payMethod: p.payMethod },
              { onSuccess: () => setDisburseTargetId(null) },
            );
          }}
          onClose={() => setDisburseTargetId(null)}
        />
      )}
    </div>
  );
}

interface EmpAgg {
  emp: string;
  dept: string;
  count: number;
  amt: number;
  overdue: boolean;
}

function ByEmployeeRows({ data }: { data: readonly Advance[] }) {
  const open = data.filter((a) => a.status === 'open' || a.status === 'overdue');
  const byEmp: Record<string, EmpAgg> = {};
  open.forEach((a) => {
    const cur = byEmp[a.emp] ?? { emp: a.emp, dept: a.dept, count: 0, amt: 0, overdue: false };
    cur.count++;
    cur.amt += a.paid - a.cleared_amt;
    if (a.status === 'overdue') cur.overdue = true;
    byEmp[a.emp] = cur;
  });
  const rows = Object.values(byEmp).sort((a, b) => b.amt - a.amt);
  return (
    <>
      {rows.map((e) => (
        <tr key={e.emp}>
          <td>
            <b>{e.emp}</b>
          </td>
          <td className="muted">{e.dept}</td>
          <td className="num">{e.count}</td>
          <td className="num tnum">
            <b>{fmt(e.amt)}</b>
          </td>
          <td>
            {e.overdue ? (
              <span className="badge negative">
                <span className="bdot" />
                เกินกำหนด
              </span>
            ) : (
              <span className="badge brand">
                <span className="bdot" />
                ปกติ
              </span>
            )}
          </td>
        </tr>
      ))}
    </>
  );
}

interface PurposeAgg extends AdvPurpose {
  count: number;
  amt: number;
}

function ByPurposeRows({ data }: { data: readonly Advance[] }) {
  const byP: Record<string, PurposeAgg> = {};
  data.forEach((a) => {
    const cur = byP[a.purpose] ?? { ...getPurpose(a.purpose), count: 0, amt: 0 };
    cur.count++;
    cur.amt += a.approved || a.requested;
    byP[a.purpose] = cur;
  });
  const all = Object.values(byP);
  const total = all.reduce((s, x) => s + x.amt, 0);
  const max = Math.max(1, ...all.map((x) => x.amt));
  const rows = all.sort((a, b) => b.amt - a.amt);
  return (
    <>
      {rows.map((p) => (
        <tr key={p.code}>
          <td>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: p.tone }} />
              {p.label}
            </span>
          </td>
          <td className="num">{p.count}</td>
          <td className="num tnum">
            <b>{fmt(p.amt)}</b>
          </td>
          <td>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div
                style={{
                  flex: 1,
                  height: 6,
                  borderRadius: 3,
                  background: '#f0f0f3',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${(p.amt / max) * 100}%`,
                    height: '100%',
                    background: p.tone,
                  }}
                />
              </div>
              <span className="meta" style={{ minWidth: 40, textAlign: 'right' }}>
                {total ? ((p.amt / total) * 100).toFixed(1) : 0}%
              </span>
            </div>
          </td>
        </tr>
      ))}
    </>
  );
}

// ============================================================
// M5 — payroll-deduction view (report-only, no JV). Lists the advances whose
// write-off flagged a residual the employee still owes; HR deducts it via payroll
// out-of-band and marks each "ทำจ่ายแล้ว" to clear the flag. Surfaced beside the
// other report cards (no page redesign).
// ============================================================

function PayrollDeductionCard({ companyId }: { companyId: string | undefined }) {
  const query = usePayrollDeductions(companyId);
  const clearDeduction = useClearPayrollDeduction(companyId);
  const rows = query.data ?? [];
  return (
    <div className="card" data-testid="payroll-deduction-section">
      <div className="card-head">
        <h3>หักจากเงินเดือน (ตัดจำหน่าย)</h3>
        <span className="meta">รอ HR หักผ่าน Payroll · ไม่ลงบัญชีเพิ่ม</span>
      </div>
      <div className="card-body flush">
        <table className="tbl">
          <thead>
            <tr>
              <th>เลขที่ใบเบิก</th>
              <th>พนักงาน</th>
              <th className="num">ยอดหัก</th>
              <th>วันที่ตัดจำหน่าย</th>
              <th style={{ width: 110 }}></th>
            </tr>
          </thead>
          <tbody>
            {query.isPending ? (
              <tr>
                <td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  กำลังโหลด…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  ไม่มีรายการรอหักจากเงินเดือน
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id}>
                  <td className="code">{r.voucherNo}</td>
                  <td>{r.employeeName}</td>
                  <td className="num tnum">
                    <b>{fmt(r.pendingPayrollAmount)}</b>
                  </td>
                  <td>
                    {r.writtenOffAt ? fmtDate(r.writtenOffAt) : <span className="meta">—</span>}
                  </td>
                  <td>
                    <button
                      className="btn sm ghost"
                      data-testid={`payroll-clear-${r.id}`}
                      disabled={clearDeduction.isPending}
                      onClick={() => clearDeduction.mutate(r.id)}
                    >
                      ทำจ่ายแล้ว
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================
// M5 — taxable per-diem report (report-only, no JV). Lists the PER_DIEM/MILEAGE
// clearance lines whose claimed rate exceeded the civil-service ceiling
// (taxablePerDiemExcess > 0) so HR can pick the excess up for ภ.ง.ด.1 (flagged at
// clearance but NOT withheld). Surfaced beside the payroll-deduction card (no page
// redesign — mirrors PayrollDeductionCard).
// ============================================================

function PerDiemTaxableCard({ companyId }: { companyId: string | undefined }) {
  const query = usePerDiemTaxable(companyId);
  const rows = query.data ?? [];
  return (
    <div className="card" data-testid="taxable-perdiem-section">
      <div className="card-head">
        <h3>เบี้ยเลี้ยงส่วนเกิน (ภ.ง.ด.1)</h3>
        <span className="meta">ส่งให้ HR รวมเป็นเงินได้พนักงาน · ไม่ลงบัญชีเพิ่ม</span>
      </div>
      <div className="card-body flush">
        <table className="tbl">
          <thead>
            <tr>
              <th>เลขที่ใบเบิก</th>
              <th>พนักงาน</th>
              <th>รายละเอียด</th>
              <th className="num">ส่วนเกินที่ต้องเสียภาษี</th>
              <th>วันที่เคลียร์</th>
            </tr>
          </thead>
          <tbody>
            {query.isPending ? (
              <tr>
                <td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  กำลังโหลด…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  ไม่มีรายการเบี้ยเลี้ยงส่วนเกิน
                </td>
              </tr>
            ) : (
              rows.map((r, i) => (
                <tr key={`${r.advanceVoucherNo}-${i}`}>
                  <td className="code">{r.advanceVoucherNo}</td>
                  <td>{r.employeeName}</td>
                  <td style={{ maxWidth: 280, whiteSpace: 'normal' }}>{r.description}</td>
                  <td className="num tnum">
                    <b>{fmt(r.taxablePerDiemExcess, 2)}</b>
                  </td>
                  <td>{r.clearDate ? fmtDate(r.clearDate) : <span className="meta">—</span>}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================
// Request modal — wires POST /advances
// ============================================================

function AdvanceRequestModal({
  companyId,
  onClose,
}: {
  companyId: string | undefined;
  onClose: () => void;
}) {
  const employeesQuery = useEmployees(companyId);
  const createAdvance = useCreateAdvance(companyId);
  const submitAdvance = useSubmitAdvance(companyId);

  const [purpose, setPurpose] = useState<AdvancePurposeCategory>('TRAVEL_LOCAL');
  const [employeeId, setEmployeeId] = useState<string>('');
  const [branchId, setBranchId] = useState<string>('');
  const [departmentId, setDepartmentId] = useState<string>('');
  const [purposeText, setPurposeText] = useState('');
  const [advanceDate, setAdvanceDate] = useState(asOfToday());
  // Default deadline = +7 days per company policy.
  const [deadline, setDeadline] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  });
  const [breakdown, setBreakdown] = useState<BreakdownItem[]>([
    { label: 'ค่าเดินทาง (ตั๋ว/น้ำมัน)', amt: 0 },
    { label: 'ค่าที่พัก', amt: 0 },
    { label: 'ค่าอาหาร / เบี้ยเลี้ยง', amt: 0 },
    { label: 'ค่าใช้จ่ายเบ็ดเตล็ด', amt: 0 },
  ]);
  const total = breakdown.reduce((s, b) => s + b.amt, 0);

  // M4 — soft, warn-only precheck: does this employee already hold an open advance,
  // or does the requested amount exceed their per-employee limit? Renders a yellow
  // banner below but NEVER blocks submit (server is authoritative; this is advisory).
  const precheck = useAdvancePrecheck(companyId, employeeId || undefined, total);
  const precheckData = precheck.data;
  const showPrecheck = !!precheckData && (precheckData.hasOpenAdvance || precheckData.exceedsLimit);

  const submit = async () => {
    if (!companyId || !employeeId || total <= 0 || !purposeText.trim()) return;
    const body: CreateEmployeeAdvance = {
      advanceDate,
      employeeId,
      purpose: purposeText,
      purposeCategory: purpose,
      amount: total,
      clearDeadlineDate: deadline,
      ...(branchId ? { branchId } : {}),
      ...(departmentId ? { departmentId } : {}),
    };
    // Create the DRAFT then immediately submit it into the approval chain so it
    // lands in the "รออนุมัติ" lane (PENDING) in one action.
    try {
      const created = await createAdvance.mutateAsync({
        data: body,
        idempotencyKey: `adv-create-${crypto.randomUUID()}`,
      });
      await submitAdvance.mutateAsync(created.id);
      onClose();
    } catch {
      // surfaced via createAdvance.isError below
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 780,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              ขอเบิกเงินทดรองจ่าย
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              เลขที่ใบเบิก: ระบบจะออกให้อัตโนมัติเมื่อบันทึก (AV-YYMM-NNNNNN)
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        <div
          style={{
            padding: 22,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            overflow: 'auto',
          }}
        >
          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 8,
              }}
            >
              วัตถุประสงค์ในการเบิก
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
              {ADV_PURPOSES.map((p) => (
                <div
                  key={p.code}
                  onClick={() => setPurpose(p.code)}
                  style={{
                    border:
                      purpose === p.code ? '2px solid var(--brand)' : '1.5px solid var(--border)',
                    background: purpose === p.code ? 'var(--brand-tint)' : 'white',
                    borderRadius: 8,
                    padding: '10px 10px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: p.tone,
                      }}
                    />
                    <span style={{ fontSize: 14, fontWeight: purpose === p.code ? 600 : 500 }}>
                      {p.label}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              พนักงานผู้ขอเบิก
            </label>
            <select
              className="select"
              data-testid="request-employee"
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            >
              <option value="">— เลือกพนักงาน —</option>
              {(employeesQuery.data ?? []).map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nameTh} ({e.position})
                </option>
              ))}
            </select>

            {showPrecheck && precheckData && (
              <div
                data-testid="precheck-warning"
                style={{
                  marginTop: 8,
                  padding: '10px 12px',
                  background: '#fef3e8',
                  borderRadius: 8,
                  border: '1px solid #f0dab0',
                  fontSize: 13,
                  lineHeight: 1.55,
                  color: '#92400e',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                {precheckData.hasOpenAdvance && (
                  <span>⚠️ พนักงานมีเงินทดรองค้างอยู่ {precheckData.openCount} รายการ</span>
                )}
                {precheckData.exceedsLimit && (
                  <span>
                    ⚠️ ยอดขอเบิกเกินวงเงิน
                    {precheckData.advanceLimit != null
                      ? ` ${fmt(precheckData.advanceLimit)} บาท`
                      : ''}
                  </span>
                )}
                <span className="meta" style={{ color: '#92400e' }}>
                  (เป็นการแจ้งเตือนเท่านั้น — ยังส่งขออนุมัติได้)
                </span>
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                สาขา
              </label>
              <BranchSelect
                companyId={companyId}
                value={branchId}
                onChange={setBranchId}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                หน่วยงาน (Cost Center)
              </label>
              <DepartmentSelect
                companyId={companyId}
                value={departmentId}
                onChange={setDepartmentId}
                branchId={branchId}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
          </div>

          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              รายละเอียดภารกิจ
            </label>
            <textarea
              className="input"
              rows={2}
              placeholder="เช่น พบลูกค้าโครงการ MRT สายสีส้ม ที่บางขุนนนท์ — 2 วัน"
              value={purposeText}
              onChange={(e) => setPurposeText(e.target.value)}
              style={{
                width: '100%',
                padding: 10,
                fontSize: 14,
                resize: 'vertical',
                minHeight: 60,
              }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                วันที่ขอเบิก
              </label>
              <input
                className="input"
                type="date"
                value={advanceDate}
                onChange={(e) => setAdvanceDate(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                วันที่กลับ (อ้างอิง)
              </label>
              <input
                className="input"
                type="date"
                defaultValue={advanceDate}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                กำหนดเคลียร์ภายใน
              </label>
              <input
                className="input"
                type="date"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
          </div>

          <div
            style={{
              padding: '14px 16px',
              background: '#fafafa',
              borderRadius: 8,
              border: '1px solid var(--border)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 10,
              }}
            >
              <div className="section-title" style={{ margin: 0 }}>
                ประมาณการค่าใช้จ่าย
              </div>
              <button
                className="btn sm ghost"
                style={{ height: 26, fontSize: 13 }}
                onClick={() => setBreakdown([...breakdown, { label: '', amt: 0 }])}
              >
                <Icon name="plus" size={11} /> เพิ่มรายการ
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {breakdown.map((b, i) => (
                <div
                  key={i}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 180px 30px',
                    gap: 8,
                    alignItems: 'center',
                  }}
                >
                  <input
                    className="input"
                    value={b.label}
                    onChange={(e) =>
                      setBreakdown(
                        breakdown.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)),
                      )
                    }
                    style={{ height: 34, fontSize: 14 }}
                  />
                  <input
                    className="input tnum"
                    type="number"
                    value={b.amt}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value) || 0;
                      setBreakdown(breakdown.map((x, j) => (j === i ? { ...x, amt: v } : x)));
                    }}
                    style={{ height: 34, fontSize: 14, textAlign: 'right', fontWeight: 500 }}
                  />
                  <button
                    className="btn ghost"
                    style={{ height: 34, padding: 0 }}
                    onClick={() => setBreakdown(breakdown.filter((_, j) => j !== i))}
                  >
                    <Icon name="x" size={14} />
                  </button>
                </div>
              ))}
            </div>
            <div
              style={{
                marginTop: 12,
                paddingTop: 12,
                borderTop: '1px solid var(--border-strong)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span style={{ fontWeight: 600 }}>รวมทั้งสิ้น (ขอเบิก)</span>
              <span
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: 'var(--brand)',
                  fontFamily: 'var(--font-display)',
                }}
                className="tnum"
              >
                {fmt(total)} บาท
              </span>
            </div>
          </div>

          <div
            style={{
              padding: '12px 14px',
              background: '#fef3e8',
              borderRadius: 8,
              border: '1px solid #f0dab0',
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
            }}
          >
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                background: 'var(--warning)',
                color: 'white',
                display: 'grid',
                placeItems: 'center',
                fontSize: 14,
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              !
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.55 }}>
              <b>เงื่อนไขนโยบายเงินทดรอง:</b> ต้องเคลียร์ภายใน 7 วันหลังกลับจากภารกิจ
              พร้อมแนบใบเสร็จทุกรายการที่ ≥ 500 บาท ·
              หากใช้ไม่หมดต้องคืนเงินส่วนต่างภายในวันเดียวกัน · ใบเบิกค้างเกิน 30
              วันจะระงับการเบิกใบใหม่อัตโนมัติ
            </div>
          </div>

          {createAdvance.isError && (
            <div className="badge negative" style={{ fontSize: 13 }}>
              บันทึกล้มเหลว: {createAdvance.error.message}
            </div>
          )}
        </div>

        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 10,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn primary"
              onClick={() => void submit()}
              disabled={
                createAdvance.isPending ||
                submitAdvance.isPending ||
                !employeeId ||
                total <= 0 ||
                !purposeText.trim()
              }
            >
              <Icon name="check" size={14} /> ส่งขออนุมัติ
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Clearance modal — wires POST /advances/:id/clear
// ============================================================

type InputVatDeductible = 'CREDITABLE' | 'FORBIDDEN' | 'NONE';
type DocumentType = 'TAX_INVOICE_FULL' | 'RECEIPT' | 'PETTY_VOUCHER' | 'NONE';

// Per-line clearance form. `cat` is kept for the legacy free-text label; the M3
// tax capture adds the resolved category id + document/vendor/VAT-WHT fields plus
// manual-override edit state. Tax amounts are recomputed live (client preview);
// the server stays the source of truth and recomputes on POST.
interface ClearLineForm {
  // Stable synthetic key used only as the React list key, so removing a middle
  // row doesn't reassign a row's identity (and its per-row SearchSelect open/
  // search state) to its neighbour the way an array-index key would. Never sent
  // to the server — buildLine() whitelists the wire fields explicitly.
  _uid: string;
  date: string;
  cat: string;
  description: string;
  expenseAccountCode: string;
  amount: number;
  receiptRef?: string;
  // M3 — tax capture
  categoryId: string;
  documentType: DocumentType;
  vendorTaxId: string;
  vendorName: string;
  inputVatDeductible: InputVatDeductible;
  whtTypeCode: string | null;
  whtFormCode: string | null;
  whtRate: number | null;
  // manual override: when true the user edited VAT/WHT and we send explicit amounts
  taxEdited: boolean;
  vatAmountManual: number | null;
  whtAmountManual: number | null;
  // M4 — the MinIO storage key of the uploaded receipt backing this line (set by the
  // OCR upload). The ≥500 / CREDITABLE gates require it; it is sent in the clear
  // payload. `dirtyFields` tracks which fields the user manually edited so an OCR
  // prefill never clobbers a value the user already typed.
  attachmentStorageKey: string | null;
  dirtyFields: Partial<Record<'amount' | 'vendorTaxId' | 'documentType' | 'vatAmount', boolean>>;
  // M5 — line discriminator + per-diem/mileage allowance fields. RECEIPT_EXPENSE =
  // the M3/M4 tax-aware receipt path. PER_DIEM/MILEAGE = a non-receipt allowance:
  // the user picks a PerDiemRate + a qty (วัน/กม.); allowance = rate×qty is computed
  // client-side and the tax/receipt controls + gates are hidden/skipped (mirrors the
  // server). dupSeq>0 + dupOverrideReason = an audited re-submit of a duplicate
  // receipt (set only via the dedup-override flow on a 409).
  lineType: ClearLineType;
  perDiemRateId: string;
  perDiemQty: number;
  dupSeq: number;
  dupOverrideReason: string;
}

type ClearLineType = 'RECEIPT_EXPENSE' | 'PER_DIEM' | 'MILEAGE';

const LINE_TYPE_OPTIONS: readonly { value: ClearLineType; label: string }[] = [
  { value: 'RECEIPT_EXPENSE', label: 'รับเงินตามใบเสร็จ' },
  { value: 'PER_DIEM', label: 'เบี้ยเลี้ยง' },
  { value: 'MILEAGE', label: 'ค่าพาหนะ (กม.)' },
];

const DOC_TYPE_OPTIONS: readonly { value: DocumentType; label: string }[] = [
  { value: 'NONE', label: '— ไม่ระบุ —' },
  { value: 'TAX_INVOICE_FULL', label: 'ใบกำกับภาษีเต็มรูป' },
  { value: 'RECEIPT', label: 'ใบเสร็จรับเงิน' },
  { value: 'PETTY_VOUCHER', label: 'ใบสำคัญจ่าย' },
];

const VAT_RATE = 7;

// Exact half-up to 2dp that MATCHES the server (decimal.js ROUND_HALF_UP) at
// half-cent money boundaries. The old `Math.round((n+EPSILON)*100)/100` diverged
// for values like 18.705 (float 18.705*100 = 1870.4999… → Math.round → 18.70,
// whereas the server rounds 18.71). Scale to integer cents with a tiny epsilon
// to absorb binary-float representation error, then floor(+0.5) = half-up.
function round2(n: number): number {
  const sign = n < 0 ? -1 : 1;
  const cents = Math.floor(Math.abs(n) * 100 + 0.5 + 1e-9);
  return (sign * cents) / 100;
}

export interface LineTaxPreview {
  vatBase: number;
  vatAmount: number;
  whtBase: number;
  whtAmount: number;
  expenseDebit: number;
  // advance outstanding consumed by this line = gross − wht (the employee keeps the
  // withheld cash, owed back to RD), which is what gets credited to EMPLOYEE_ADVANCE.
  netConsumed: number;
}

// Client-side mirror of the api computeLineTax (treatment A) for live preview.
// Pure + deterministic so it is unit-friendly. Honors a manual VAT/WHT override.
export function computeLineTaxPreview(line: ClearLineForm): LineTaxPreview {
  const gross = Number.isFinite(line.amount) ? line.amount : 0;

  let vatBase = 0;
  let vatAmount = 0;
  if (line.inputVatDeductible === 'CREDITABLE') {
    vatBase = round2(gross / (1 + VAT_RATE / 100));
    vatAmount = round2(gross - vatBase);
    if (line.taxEdited && line.vatAmountManual !== null) {
      vatAmount = round2(line.vatAmountManual);
      vatBase = round2(gross - vatAmount);
    }
  }

  // FORBIDDEN still books the full gross as expense (non-creditable VAT folded in).
  const expenseDebit = line.inputVatDeductible === 'CREDITABLE' ? vatBase : gross;
  // whtBase excludes VAT. The line is VAT-applicable when CREDITABLE or FORBIDDEN
  // (7% embedded) → WHT is withheld on the pre-VAT fee (RD practice), so strip the
  // embedded 7%. Only NONE (no VAT) keeps whtBase = gross. Mirrors the server.
  const whtBase =
    line.inputVatDeductible === 'CREDITABLE'
      ? vatBase
      : line.inputVatDeductible === 'FORBIDDEN'
        ? round2(gross / (1 + VAT_RATE / 100))
        : gross;
  const rate = line.whtRate ?? 0;
  let whtAmount = rate > 0 ? round2((whtBase * rate) / 100) : 0;
  if (line.taxEdited && line.whtAmountManual !== null) {
    whtAmount = round2(line.whtAmountManual);
  }

  return {
    vatBase,
    vatAmount,
    whtBase,
    whtAmount,
    expenseDebit,
    netConsumed: round2(gross - whtAmount),
  };
}

// FIX 1 — parse the colliding clearance-line description out of the server's dedup
// 409 message (`…เพื่อบันทึกซ้ำ: "<description>"`) so the override is scoped to that
// line only. Returns the trimmed description, or null when the message carries no
// named line (caller then falls back to a blanket override).
export function parseDupLineDescription(message: string): string | null {
  const m = /:\s*"([^"]*)"\s*$/.exec(message);
  return m ? m[1]!.trim() : null;
}

// M5 — client mirror of the api computePerDiemLine for the PER_DIEM/MILEAGE line
// preview. allowance = rate×qty (the line amount sent to the server); taxableExcess
// = max(0, (rate−ceilingRate)×qty) is flagged for HR/ภ.ง.ด.1 but NOT withheld.
export function computePerDiemPreview(rate: number, ceilingRate: number, qty: number) {
  const q = Number.isFinite(qty) && qty > 0 ? qty : 0;
  const allowance = round2(rate * q);
  const taxableExcess = round2(Math.max(0, (rate - ceilingRate) * q));
  return { allowance, taxableExcess };
}

function AdvanceClearModal({
  companyId,
  advance,
  onClose,
}: {
  companyId: string | undefined;
  advance: Advance;
  onClose: () => void;
}) {
  const clearAdvance = useClearAdvance(companyId);
  const coaQuery = useCoa(companyId);
  const categoriesQuery = useAdvanceExpenseCategories(companyId);
  const categories = useMemo<AdvanceExpenseCategory[]>(
    () => categoriesQuery.data ?? [],
    [categoriesQuery.data],
  );
  const categoryById = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);

  // M5 — per-diem / mileage allowance master (the lineType=PER_DIEM/MILEAGE picker).
  const perDiemRatesQuery = usePerDiemRates(companyId);
  const perDiemRates = useMemo<PerDiemRate[]>(
    () => perDiemRatesQuery.data ?? [],
    [perDiemRatesQuery.data],
  );
  const perDiemRateById = useMemo(
    () => new Map(perDiemRates.map((r) => [r.id, r])),
    [perDiemRates],
  );

  // Postable expense / cost accounts are the valid targets for clearance lines:
  // every real account is a leaf (isControl=false); type COST/EXPENSE carries the
  // Dr-nature expense codes. Sourced from the live COA — no hardcoded code shape.
  const expenseAccounts = useMemo(
    () =>
      (coaQuery.data ?? []).filter(
        (c) => c.isActive && !c.isControl && (c.type === 'COST' || c.type === 'EXPENSE'),
      ),
    [coaQuery.data],
  );
  // First resolved expense code — used to seed the default clearance lines so the
  // <select> always lands on a real, existing account (no dangling code).
  const defaultExpenseCode = expenseAccounts[0]?.code ?? '';

  // A fresh, untaxed line (no category → NONE deductibility = behaves like before).
  const blankLine = (): ClearLineForm => ({
    _uid: crypto.randomUUID(),
    date: asOfToday(),
    cat: '',
    description: '',
    expenseAccountCode: defaultExpenseCode,
    amount: 0,
    categoryId: '',
    documentType: 'NONE',
    vendorTaxId: '',
    vendorName: '',
    inputVatDeductible: 'NONE',
    whtTypeCode: null,
    whtFormCode: null,
    whtRate: null,
    taxEdited: false,
    vatAmountManual: null,
    whtAmountManual: null,
    attachmentStorageKey: null,
    dirtyFields: {},
    lineType: 'RECEIPT_EXPENSE',
    perDiemRateId: '',
    perDiemQty: 0,
    dupSeq: 0,
    dupOverrideReason: '',
  });

  const [clearDate, setClearDate] = useState(asOfToday());
  const uploadReceipt = useUploadAdvanceReceipt(companyId);
  // Per-line upload pending guard (so only the active row shows a spinner).
  const [uploadingLine, setUploadingLine] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [items, setItems] = useState<ClearLineForm[]>(() => [blankLine()]);

  // Apply a category to a line: default the expense code + VAT/WHT treatment from
  // the master (mirrors the server's category resolution). Clears any manual edit.
  const applyCategory = (i: number, categoryId: string) => {
    setItems((prev) =>
      prev.map((x, j) => {
        if (j !== i) return x;
        const cat = categoryById.get(categoryId);
        if (!cat) {
          return {
            ...x,
            categoryId: '',
            inputVatDeductible: 'NONE',
            whtTypeCode: null,
            whtFormCode: null,
            whtRate: null,
            taxEdited: false,
            vatAmountManual: null,
            whtAmountManual: null,
          };
        }
        return {
          ...x,
          categoryId,
          expenseAccountCode: cat.glExpenseCode || x.expenseAccountCode,
          inputVatDeductible: cat.vatApplicable ? 'CREDITABLE' : 'NONE',
          whtTypeCode: cat.whtTypeCode ?? null,
          whtFormCode: cat.whtFormCode ?? null,
          whtRate: cat.whtRate ?? null,
          taxEdited: false,
          vatAmountManual: null,
          whtAmountManual: null,
        };
      }),
    );
  };

  const patchLine = (i: number, patch: Partial<ClearLineForm>) =>
    setItems((prev) => prev.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  // M5 — recompute the PER_DIEM/MILEAGE line `amount` from its rate×qty so the modal
  // total/diff + the submit payload pick up the allowance (the line carries no
  // separate amount input). RECEIPT_EXPENSE lines keep their typed amount.
  const syncPerDiemAmount = (x: ClearLineForm): ClearLineForm => {
    if (x.lineType === 'RECEIPT_EXPENSE') return x;
    const r = perDiemRateById.get(x.perDiemRateId);
    if (!r) return { ...x, amount: 0 };
    const { allowance } = computePerDiemPreview(r.rate, r.ceilingRate, x.perDiemQty);
    return { ...x, amount: allowance, expenseAccountCode: r.glExpenseCode || x.expenseAccountCode };
  };

  // Switch a line's type. Moving to PER_DIEM/MILEAGE clears the tax/receipt state
  // (allowances have none) and resets the amount; moving back to RECEIPT_EXPENSE
  // clears the per-diem picks.
  const setLineType = (i: number, lineType: ClearLineType) =>
    setItems((prev) =>
      prev.map((x, j) => {
        if (j !== i) return x;
        if (lineType === 'RECEIPT_EXPENSE') {
          return { ...x, lineType, perDiemRateId: '', perDiemQty: 0 };
        }
        return syncPerDiemAmount({
          ...x,
          lineType,
          // drop the receipt-path tax/attachment state
          categoryId: '',
          inputVatDeductible: 'NONE',
          whtTypeCode: null,
          whtFormCode: null,
          whtRate: null,
          taxEdited: false,
          vatAmountManual: null,
          whtAmountManual: null,
          documentType: 'NONE',
          vendorTaxId: '',
          vendorName: '',
          attachmentStorageKey: null,
          dupSeq: 0,
          dupOverrideReason: '',
        });
      }),
    );

  const applyPerDiemRate = (i: number, perDiemRateId: string) =>
    setItems((prev) =>
      prev.map((x, j) => (j === i ? syncPerDiemAmount({ ...x, perDiemRateId }) : x)),
    );

  const setPerDiemQty = (i: number, perDiemQty: number) =>
    setItems((prev) => prev.map((x, j) => (j === i ? syncPerDiemAmount({ ...x, perDiemQty }) : x)));

  // Manual-edit patch — marks the touched field dirty so a later OCR prefill won't
  // overwrite it (the override-respect rule).
  const markDirty = (
    i: number,
    field: 'amount' | 'vendorTaxId' | 'documentType',
    patch: Partial<ClearLineForm>,
  ) =>
    setItems((prev) =>
      prev.map((x, j) =>
        j === i ? { ...x, ...patch, dirtyFields: { ...x.dirtyFields, [field]: true } } : x,
      ),
    );

  // M4 — upload a receipt for a clearance line → MinIO + Claude OCR. On success,
  // store the storageKey and merge the OCR `suggested` prefill into the line, but
  // ONLY into fields the user has NOT manually edited (dirtyFields). Numbers/strings
  // from `suggested` are applied as-is; documentType only when the OCR is confident
  // (not NONE).
  const onUpload = async (i: number, file: File) => {
    if (!advance.apiId) return;
    // Client-side guard mirroring the server Fastify limits — reject oversized or
    // non-image/PDF files BEFORE the multipart round-trip, with a localized error.
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('ไฟล์ใหญ่เกิน 10MB');
      return;
    }
    if (!/^(image\/|application\/pdf)/.test(file.type)) {
      setUploadError('รองรับเฉพาะรูปภาพหรือ PDF');
      return;
    }
    setUploadingLine(i);
    setUploadError(null);
    try {
      const result = await uploadReceipt.mutateAsync({ id: advance.apiId, file });
      setItems((prev) =>
        prev.map((x, j) => {
          if (j !== i) return x;
          const s = result.suggested;
          const next: ClearLineForm = { ...x, attachmentStorageKey: result.storageKey };
          if (!x.dirtyFields.amount && s.amount != null && s.amount > 0) next.amount = s.amount;
          if (!x.dirtyFields.vendorTaxId && s.vendorTaxId) next.vendorTaxId = s.vendorTaxId;
          if (!x.dirtyFields.documentType && s.documentType !== 'NONE')
            next.documentType = s.documentType;
          // Apply the extracted VAT only when the OCR is confident (non-null > 0),
          // the user hasn't taken manual control of the tax figures (vatAmount dirty),
          // and the line is actually VAT-creditable (NONE/FORBIDDEN have no VAT leg).
          // Setting an explicit vatAmount opens the override (taxEdited) so the server
          // honors the OCR figure — same path a manual VAT edit takes.
          if (
            !x.dirtyFields.vatAmount &&
            !x.taxEdited &&
            s.vatAmount != null &&
            s.vatAmount > 0 &&
            next.inputVatDeductible === 'CREDITABLE'
          ) {
            next.taxEdited = true;
            next.vatAmountManual = s.vatAmount;
          }
          return next;
        }),
      );
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : 'อัปโหลดไม่สำเร็จ');
    } finally {
      setUploadingLine(null);
    }
  };

  const total = items.reduce((s, i) => s + i.amount, 0);
  const diff = advance.paid - total;
  const returnAmt = diff > 0 ? diff : 0;
  const reimbAmt = diff < 0 ? -diff : 0;

  // M5 — a PER_DIEM/MILEAGE line is a non-receipt allowance: it carries no VAT/WHT
  // and no third-party receipt, so it is EXCLUDED from the creditable / WHT-payee /
  // attachment gates below (mirrors the server, which skips computeLineTax + the
  // attachment gate for these lines).
  const isReceiptLine = (i: ClearLineForm) => i.lineType === 'RECEIPT_EXPENSE';

  // CREDITABLE input VAT requires a full tax invoice + the vendor's taxId (the M4
  // hard-gate, previewed client-side so submit blocks before a 400 round-trip).
  const creditableError = items.some(
    (i) =>
      isReceiptLine(i) &&
      i.inputVatDeductible === 'CREDITABLE' &&
      (i.documentType !== 'TAX_INVOICE_FULL' || !i.vendorTaxId.trim()),
  );

  // Any line that ACTUALLY withholds WHT (computed/overridden whtAmount > 0) needs
  // the payee's tax id — the ภ.ง.ด./50ทวิ certificate is keyed on it. Mirrors a
  // server guard (rejects whtAmount>0 with no vendorTaxId) so a FORBIDDEN/NONE line
  // that still withholds (e.g. RENTAL 5% on a RECEIPT) blocks here before a 400.
  // A de-minimis line (whtAmount === 0) is NOT gated.
  const whtPayeeError = items.some(
    (i) => isReceiptLine(i) && computeLineTaxPreview(i).whtAmount > 0 && !i.vendorTaxId.trim(),
  );

  // M4 — attachment gate (client mirror of the server clearAdvance gates): any
  // RECEIPT_EXPENSE line with amount ≥ 500 OR creditable input VAT must carry an
  // uploaded receipt (attachmentStorageKey). Per-diem/mileage allowance lines are
  // skipped (no receipt). Blocks submit before a 400 round-trip.
  const lineNeedsAttachment = (i: ClearLineForm) =>
    isReceiptLine(i) &&
    (i.amount >= 500 || i.inputVatDeductible === 'CREDITABLE') &&
    !i.attachmentStorageKey;
  const attachmentError = items.some(lineNeedsAttachment);

  // FIX 2 — client gate for degenerate per-diem / mileage lines (mirrors the server,
  // which 400s a PER_DIEM/MILEAGE line with no rate or qty ≤ 0 → allowance 0). Blocks
  // submit before the round-trip. RECEIPT_EXPENSE lines are skipped (typed amount).
  const lineIsDegeneratePerDiem = (i: ClearLineForm) =>
    i.lineType !== 'RECEIPT_EXPENSE' && (!i.perDiemRateId || !(i.perDiemQty > 0));
  const perDiemError = items.some(lineIsDegeneratePerDiem);

  // M5 — receipt-dedup override (FIX 1: per-line, not blanket). A clear-mutation 409
  // (ConflictException "ใบเสร็จซ้ำ") surfaces an inline override prompt; the user
  // supplies a reason and resubmits. The server NAMES the colliding line in the 409
  // (`…: "<description>"`) — we parse those names and apply dupSeq:1 + the reason ONLY
  // to the matching RECEIPT_EXPENSE line(s). Other receipt lines stay dupSeq:0 so a
  // SECOND genuine duplicate re-throws its own 409 and forces its own justification
  // (a blanket override would mask it). Names accumulate across retries (the server
  // throws on the first P2002 it hits); the override clears on a successful post.
  const [dedupConflict, setDedupConflict] = useState(false);
  const [dupReason, setDupReason] = useState('');
  const [dupLineDescriptions, setDupLineDescriptions] = useState<Set<string>>(() => new Set());

  // Build the per-line wire shape. PER_DIEM/MILEAGE → only lineType/rate/qty +
  // description/amount (the allowance) — NO tax/receipt fields. RECEIPT_EXPENSE →
  // the M3/M4 tax-aware shape (+ dedup escape when overriding). `override` here means
  // THIS line was named in the 409 (its description is in dupLineDescriptions).
  const buildLine = (i: ClearLineForm, override: boolean): CreateAdvanceClearanceLine => {
    if (i.lineType !== 'RECEIPT_EXPENSE') {
      return {
        description: i.description,
        expenseAccountCode: i.expenseAccountCode || defaultExpenseCode,
        amount: i.amount,
        lineType: i.lineType,
        ...(i.perDiemRateId ? { perDiemRateId: i.perDiemRateId } : {}),
        perDiemQty: i.perDiemQty,
        documentType: 'NONE',
        dupSeq: 0,
      };
    }
    const preview = computeLineTaxPreview(i);
    // Baseline = the auto-computed split with NO manual override applied. We only
    // send explicit override amounts when the user ACTUALLY changed a value from
    // this baseline (compared at 2dp). Merely opening the editor (taxEdited=true,
    // value unchanged) must NOT fabricate a forced override — let the server derive.
    const baseline = computeLineTaxPreview({ ...i, taxEdited: false });
    const vatEdited =
      i.taxEdited &&
      i.inputVatDeductible === 'CREDITABLE' &&
      i.vatAmountManual !== null &&
      round2(i.vatAmountManual) !== baseline.vatAmount;
    const whtEdited =
      i.taxEdited && i.whtAmountManual !== null && round2(i.whtAmountManual) !== baseline.whtAmount;
    return {
      description: i.description,
      expenseAccountCode: i.expenseAccountCode || defaultExpenseCode,
      amount: i.amount,
      lineType: 'RECEIPT_EXPENSE',
      documentType: i.documentType,
      ...(i.receiptRef ? { receiptRef: i.receiptRef } : {}),
      ...(i.categoryId ? { categoryId: i.categoryId } : {}),
      ...(i.vendorTaxId.trim() ? { vendorTaxId: i.vendorTaxId.trim() } : {}),
      ...(i.vendorName.trim() ? { vendorName: i.vendorName.trim() } : {}),
      ...(i.inputVatDeductible !== 'NONE' ? { inputVatDeductible: i.inputVatDeductible } : {}),
      ...(i.whtTypeCode ? { whtTypeCode: i.whtTypeCode } : {}),
      ...(i.whtFormCode === 'PND3' || i.whtFormCode === 'PND53'
        ? { whtFormCode: i.whtFormCode }
        : {}),
      // Real VAT edit → send explicit legs so the server flags taxOverridden and
      // posts the user's figures. Unchanged → omit (server derives).
      ...(vatEdited ? { vatBase: preview.vatBase, vatAmount: preview.vatAmount } : {}),
      // Real WHT edit → send explicit base+amount; unchanged → omit.
      ...(whtEdited ? { whtBase: preview.whtBase, whtAmount: preview.whtAmount } : {}),
      // M4 — carry the uploaded receipt key so the server gate + persistence see it.
      ...(i.attachmentStorageKey ? { attachmentStorageKey: i.attachmentStorageKey } : {}),
      // M5 — dedup override (one-shot): re-submit a duplicate receipt with an audited
      // reason. Applied to the receipt lines when the user confirms the override.
      dupSeq: override ? 1 : 0,
      ...(override ? { dupOverrideReason: dupReason.trim() } : {}),
    };
  };

  const doSubmit = (override: boolean) => {
    if (
      !companyId ||
      !advance.apiId ||
      items.length === 0 ||
      creditableError ||
      whtPayeeError ||
      perDiemError ||
      attachmentError
    )
      return;
    const body: ClearEmployeeAdvance = {
      clearDate,
      // FIX 1 — when overriding, a line is dedup-overridden ONLY if the server named
      // its description in a 409 (it is in dupLineDescriptions). Un-named lines stay
      // dupSeq:0 so their own (later) collision still re-throws its own 409.
      lines: items.map((i) =>
        buildLine(i, override && dupLineDescriptions.has(i.description.trim())),
      ),
      refundAmount: returnAmt,
    };
    clearAdvance.mutate(
      {
        id: advance.apiId,
        data: body,
        idempotencyKey: `adv-clear-${advance.apiId}-${crypto.randomUUID()}`,
      },
      {
        onSuccess: () => {
          setDedupConflict(false);
          setDupReason('');
          setDupLineDescriptions(new Set());
          onClose();
        },
        onError: (e: unknown) => {
          // A ConflictException (409 / "ใบเสร็จซ้ำ") → open the dedup-override prompt
          // instead of just showing the generic error. Detect by HTTP status or the
          // localized marker so the barrel-mocked tests (plain Error) also match.
          const status = (e as { status?: number })?.status;
          const msg = e instanceof Error ? e.message : '';
          if (status === 409 || msg.includes('ใบเสร็จซ้ำ')) {
            setDedupConflict(true);
            // FIX 1 — the server names the colliding line as `…: "<description>"`.
            // Add it to the override set so this resubmit overrides ONLY that line.
            // Fall back to ALL receipt-line descriptions if the message carries no
            // name (older server / generic 409) so the override still works.
            const named = parseDupLineDescription(msg);
            setDupLineDescriptions((prev) => {
              const next = new Set(prev);
              if (named) next.add(named);
              else
                items
                  .filter((i) => i.lineType === 'RECEIPT_EXPENSE')
                  .forEach((i) => next.add(i.description.trim()));
              return next;
            });
          }
        },
      },
    );
  };

  const submit = () => doSubmit(false);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 920,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              เคลียร์เงินทดรองจ่าย
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              <b style={{ color: '#3f3f46' }}>{advance.id}</b> · {advance.emp} ({advance.dept}) ·{' '}
              {advance.desc}
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        <div
          style={{
            padding: 22,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            overflow: 'auto',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4,1fr)',
              gap: 14,
              padding: '14px 16px',
              background: '#fafafa',
              borderRadius: 8,
              border: '1px solid var(--border)',
            }}
          >
            <div>
              <div className="meta">เงินทดรองที่รับ</div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  marginTop: 4,
                  fontFamily: 'var(--font-display)',
                }}
                className="tnum"
              >
                {fmt(advance.paid)}
              </div>
            </div>
            <div>
              <div className="meta">ค่าใช้จ่ายจริง (รวม)</div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  marginTop: 4,
                  color: 'var(--brand)',
                  fontFamily: 'var(--font-display)',
                }}
                className="tnum"
              >
                {fmt(total)}
              </div>
            </div>
            <div>
              <div className="meta">{diff >= 0 ? 'คืนบริษัท' : 'บริษัทจ่ายเพิ่ม'}</div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  marginTop: 4,
                  color: diff >= 0 ? 'var(--positive)' : 'var(--negative)',
                  fontFamily: 'var(--font-display)',
                }}
                className="tnum"
              >
                {diff >= 0 ? fmt(returnAmt) : fmt(reimbAmt)}
              </div>
            </div>
            <div>
              <div className="meta">วันที่เคลียร์</div>
              <input
                type="date"
                className="input"
                value={clearDate}
                onChange={(e) => setClearDate(e.target.value)}
                style={{ height: 30, fontSize: 14, marginTop: 4 }}
              />
              <div className="meta">{daysFrom(advance.date, clearDate)} วันหลังเบิก</div>
            </div>
          </div>

          <div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8,
              }}
            >
              <div className="section-title" style={{ margin: 0 }}>
                รายการค่าใช้จ่ายจริง ({items.length} รายการ)
              </div>
              <button
                className="btn sm"
                onClick={() => setItems([...items, { ...blankLine(), date: clearDate }])}
              >
                <Icon name="plus" size={11} /> เพิ่มรายการ
              </button>
            </div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th style={{ width: 130 }}>บัญชี (6xxxxxxx)</th>
                    <th>รายละเอียด</th>
                    <th style={{ width: 140 }}>เลขที่ใบเสร็จ</th>
                    <th className="num" style={{ width: 120 }}>
                      จำนวน
                    </th>
                    <th style={{ width: 40 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => {
                    const tax = computeLineTaxPreview(it);
                    // M5 — a per-diem/mileage line is an allowance, not a receipt:
                    // it has no tax / receipt gates (mirrors the server).
                    const receiptLine = it.lineType === 'RECEIPT_EXPENSE';
                    const needsTaxInvoice =
                      receiptLine &&
                      it.inputVatDeductible === 'CREDITABLE' &&
                      (it.documentType !== 'TAX_INVOICE_FULL' || !it.vendorTaxId.trim());
                    // Any withholding line needs the payee tax id for the 50ทวิ cert.
                    const needsWhtPayee =
                      receiptLine && tax.whtAmount > 0 && !it.vendorTaxId.trim();
                    // M4 — ≥500 or creditable VAT must carry an uploaded receipt.
                    const needsAttachment = lineNeedsAttachment(it);
                    // M5 — per-diem allowance preview (rate×qty + taxable excess).
                    const pdRate = perDiemRateById.get(it.perDiemRateId);
                    const pd = pdRate
                      ? computePerDiemPreview(pdRate.rate, pdRate.ceilingRate, it.perDiemQty)
                      : { allowance: 0, taxableExcess: 0 };
                    return (
                      <tr key={it._uid} data-testid={`clear-line-${i}`}>
                        <td colSpan={5} style={{ padding: 0 }}>
                          {/* M5 — line-type selector. RECEIPT_EXPENSE = receipt path
                              (tax + attachment); PER_DIEM/MILEAGE = allowance (rate×qty,
                              no tax/receipt). */}
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '6px 8px',
                              borderBottom: '1px dashed var(--border)',
                            }}
                          >
                            <span className="meta">ประเภทรายการ</span>
                            <select
                              className="select"
                              data-testid="line-type"
                              value={it.lineType}
                              onChange={(e) => setLineType(i, e.target.value as ClearLineType)}
                              style={{ height: 26, fontSize: 13, width: 200 }}
                            >
                              {LINE_TYPE_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          {/* Primary expense row — unchanged columns. */}
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '130px 1fr 140px 120px 40px',
                              alignItems: 'center',
                              gap: 0,
                            }}
                          >
                            <div style={{ padding: '6px 8px' }}>
                              <select
                                className="select"
                                value={it.expenseAccountCode || defaultExpenseCode}
                                onChange={(e) =>
                                  patchLine(i, { expenseAccountCode: e.target.value })
                                }
                                style={{ height: 28, fontSize: 13 }}
                              >
                                {expenseAccounts.map((a) => (
                                  <option key={a.code} value={a.code}>
                                    {a.code} — {a.nameTh}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div style={{ padding: '6px 8px' }}>
                              <input
                                className="input"
                                data-testid="line-description"
                                value={it.description}
                                onChange={(e) => patchLine(i, { description: e.target.value })}
                                style={{ height: 28, fontSize: 13, width: '100%' }}
                              />
                            </div>
                            <div style={{ padding: '6px 8px' }}>
                              <input
                                className="input"
                                value={it.receiptRef ?? ''}
                                onChange={(e) => patchLine(i, { receiptRef: e.target.value })}
                                style={{ height: 28, fontSize: 13, width: '100%' }}
                              />
                            </div>
                            <div style={{ padding: '6px 8px' }}>
                              <input
                                className="input tnum"
                                data-testid="line-amount"
                                type="number"
                                value={it.amount}
                                // M5 — per-diem/mileage amount is the computed allowance
                                // (rate×qty), set via the qty field below, so it is
                                // read-only here.
                                readOnly={!receiptLine}
                                onChange={(e) =>
                                  markDirty(i, 'amount', {
                                    amount: parseFloat(e.target.value) || 0,
                                  })
                                }
                                style={{
                                  height: 28,
                                  fontSize: 13,
                                  textAlign: 'right',
                                  width: '100%',
                                  ...(receiptLine ? {} : { background: '#f4f4f6' }),
                                }}
                              />
                            </div>
                            <div style={{ padding: '6px 4px' }}>
                              <button
                                className="btn ghost"
                                style={{ height: 24, padding: '0 4px' }}
                                onClick={() => setItems(items.filter((_, j) => j !== i))}
                              >
                                <Icon name="x" size={13} />
                              </button>
                            </div>
                          </div>

                          {/* M5 — per-diem / mileage allowance block (rate × qty). Shown
                              for PER_DIEM / MILEAGE lines INSTEAD of the tax + receipt
                              blocks — allowances carry no VAT/WHT and no third-party
                              receipt. */}
                          {!receiptLine && (
                            <div
                              style={{
                                padding: '8px 10px 12px',
                                background: '#fafafb',
                                borderTop: '1px dashed var(--border)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 8,
                              }}
                            >
                              <div
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: '1.6fr 1fr 1fr',
                                  gap: 8,
                                  alignItems: 'end',
                                }}
                              >
                                <div>
                                  <div className="meta" style={{ marginBottom: 3 }}>
                                    {it.lineType === 'MILEAGE'
                                      ? 'อัตราค่าพาหนะ'
                                      : 'อัตราเบี้ยเลี้ยง'}
                                  </div>
                                  <div data-testid="line-perdiem-rate">
                                    <SearchSelect
                                      value={it.perDiemRateId}
                                      onChange={(v) => applyPerDiemRate(i, v)}
                                      options={perDiemRates.map((r) => ({
                                        value: r.id,
                                        label: `${r.code} — ${r.nameTh} (${fmt(r.rate)})`,
                                      }))}
                                      getLabel={(v) => {
                                        const r = perDiemRateById.get(v);
                                        return r ? `${r.code} — ${r.nameTh} (${fmt(r.rate)})` : '';
                                      }}
                                      placeholder="— เลือกอัตรา —"
                                      ariaLabel="เลือกอัตราเบี้ยเลี้ยง/พาหนะ"
                                    />
                                  </div>
                                </div>
                                <div>
                                  <div className="meta" style={{ marginBottom: 3 }}>
                                    {it.lineType === 'MILEAGE' ? 'ระยะทาง (กม.)' : 'จำนวนวัน'}
                                  </div>
                                  <input
                                    className="input tnum"
                                    data-testid="line-perdiem-qty"
                                    type="number"
                                    value={it.perDiemQty}
                                    onChange={(e) =>
                                      setPerDiemQty(i, parseFloat(e.target.value) || 0)
                                    }
                                    style={{
                                      height: 30,
                                      fontSize: 13,
                                      textAlign: 'right',
                                      width: '100%',
                                    }}
                                  />
                                </div>
                                <div>
                                  <div className="meta" style={{ marginBottom: 3 }}>
                                    ยอดเบิก (อัตรา × จำนวน)
                                  </div>
                                  <div
                                    className="tnum"
                                    data-testid="line-perdiem-allowance"
                                    style={{ fontSize: 15, fontWeight: 600, paddingTop: 4 }}
                                  >
                                    {fmt(pd.allowance, 2)}
                                  </div>
                                </div>
                              </div>
                              {pd.taxableExcess > 0 && (
                                <div
                                  className="badge warning"
                                  data-testid="line-perdiem-excess"
                                  style={{ fontSize: 12, alignSelf: 'flex-start' }}
                                >
                                  ส่วนเกินเพดานที่ต้องเสียภาษี (ภ.ง.ด.1): {fmt(pd.taxableExcess, 2)}{' '}
                                  บาท
                                </div>
                              )}
                              {/* FIX 2 — degenerate per-diem line gate (no rate / qty ≤ 0). */}
                              {lineIsDegeneratePerDiem(it) && (
                                <div
                                  className="badge negative"
                                  data-testid="line-perdiem-error"
                                  style={{ fontSize: 12, alignSelf: 'flex-start' }}
                                >
                                  เลือกอัตราและจำนวน (วัน/กม.) &gt; 0
                                </div>
                              )}
                            </div>
                          )}

                          {/* M3 — per-line tax capture (category / doc-type / vendor / VAT+WHT). */}
                          {receiptLine && (
                            <div
                              style={{
                                padding: '8px 10px 12px',
                                background: '#fafafb',
                                borderTop: '1px dashed var(--border)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 8,
                              }}
                            >
                              <div
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: '1.4fr 1fr 1fr 1.2fr',
                                  gap: 8,
                                  alignItems: 'end',
                                }}
                              >
                                <div>
                                  <div className="meta" style={{ marginBottom: 3 }}>
                                    ประเภทค่าใช้จ่าย
                                  </div>
                                  <SearchSelect
                                    value={it.categoryId}
                                    onChange={(v) => applyCategory(i, v)}
                                    options={categories.map((c) => ({
                                      value: c.id,
                                      label: `${c.code} — ${c.nameTh}`,
                                    }))}
                                    getLabel={(v) => {
                                      const c = categoryById.get(v);
                                      return c ? `${c.code} — ${c.nameTh}` : '';
                                    }}
                                    placeholder="— เลือกประเภทค่าใช้จ่าย —"
                                  />
                                </div>
                                <div>
                                  <div className="meta" style={{ marginBottom: 3 }}>
                                    ประเภทเอกสาร
                                  </div>
                                  <select
                                    className="select"
                                    data-testid="line-doctype"
                                    value={it.documentType}
                                    onChange={(e) =>
                                      markDirty(i, 'documentType', {
                                        documentType: e.target.value as DocumentType,
                                      })
                                    }
                                    style={{ height: 30, fontSize: 13, width: '100%' }}
                                  >
                                    {DOC_TYPE_OPTIONS.map((o) => (
                                      <option key={o.value} value={o.value}>
                                        {o.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                {/* Deductibility — only when the category is VAT-applicable. Lets a
                                  VAT expense paid with only a receipt (no full tax invoice) be
                                  marked ภาษีซื้อต้องห้าม (FORBIDDEN: Dr Expense gross, no VAT
                                  claim, bypasses the full-tax-invoice gate). Default CREDITABLE. */}
                                {it.inputVatDeductible !== 'NONE' && (
                                  <div>
                                    <div className="meta" style={{ marginBottom: 3 }}>
                                      ภาษีซื้อ
                                    </div>
                                    <select
                                      className="select"
                                      data-testid="line-deductible"
                                      value={it.inputVatDeductible}
                                      onChange={(e) =>
                                        patchLine(i, {
                                          inputVatDeductible: e.target.value as InputVatDeductible,
                                          // switching deductibility invalidates any manual VAT/WHT
                                          // override (the base changes) — reset to auto.
                                          taxEdited: false,
                                          vatAmountManual: null,
                                          whtAmountManual: null,
                                        })
                                      }
                                      style={{ height: 30, fontSize: 13, width: '100%' }}
                                    >
                                      <option value="CREDITABLE">เครดิตภาษีซื้อได้</option>
                                      <option value="FORBIDDEN">ภาษีซื้อต้องห้าม</option>
                                    </select>
                                  </div>
                                )}
                                <div>
                                  <div className="meta" style={{ marginBottom: 3 }}>
                                    เลขประจำตัวผู้เสียภาษีผู้ขาย
                                  </div>
                                  <input
                                    className="input"
                                    data-testid="line-vendor-taxid"
                                    value={it.vendorTaxId}
                                    onChange={(e) =>
                                      markDirty(i, 'vendorTaxId', { vendorTaxId: e.target.value })
                                    }
                                    style={{ height: 30, fontSize: 13, width: '100%' }}
                                  />
                                </div>
                                <div>
                                  <div className="meta" style={{ marginBottom: 3 }}>
                                    ชื่อผู้ขาย
                                  </div>
                                  <input
                                    className="input"
                                    data-testid="line-vendor-name"
                                    value={it.vendorName}
                                    onChange={(e) => patchLine(i, { vendorName: e.target.value })}
                                    style={{ height: 30, fontSize: 13, width: '100%' }}
                                  />
                                </div>
                              </div>

                              <div
                                style={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 16,
                                  alignItems: 'center',
                                  fontSize: 13,
                                }}
                              >
                                <span className="meta">
                                  ฐานภาษี:{' '}
                                  <b className="tnum" data-testid="line-base">
                                    {fmt(tax.expenseDebit, 2)}
                                  </b>
                                </span>
                                <span className="meta">
                                  VAT 7%:{' '}
                                  <b className="tnum" data-testid="line-vat">
                                    {fmt(tax.vatAmount, 2)}
                                  </b>
                                </span>
                                <span className="meta">
                                  หัก ณ ที่จ่าย{it.whtRate ? ` ${it.whtRate}%` : ''}:{' '}
                                  {it.taxEdited ? (
                                    <input
                                      className="input tnum"
                                      data-testid="line-wht-input"
                                      type="number"
                                      value={it.whtAmountManual ?? tax.whtAmount}
                                      onChange={(e) =>
                                        patchLine(i, {
                                          taxEdited: true,
                                          whtAmountManual: parseFloat(e.target.value) || 0,
                                        })
                                      }
                                      style={{
                                        height: 24,
                                        width: 90,
                                        fontSize: 13,
                                        textAlign: 'right',
                                        display: 'inline-block',
                                      }}
                                    />
                                  ) : (
                                    <b className="tnum" data-testid="line-wht">
                                      {fmt(tax.whtAmount, 2)}
                                    </b>
                                  )}
                                </span>
                                <span className="meta">
                                  ตัดยอดทดรอง:{' '}
                                  <b className="tnum" data-testid="line-net">
                                    {fmt(tax.netConsumed, 2)}
                                  </b>
                                </span>
                                {it.taxEdited ? (
                                  <span className="badge warning" style={{ fontSize: 12 }}>
                                    แก้ไขแล้ว
                                  </span>
                                ) : (
                                  <button
                                    className="btn sm ghost"
                                    style={{ height: 24, fontSize: 12 }}
                                    // Opening the tax editor = the user is taking manual
                                    // control of VAT/WHT → mark vatAmount dirty so a later
                                    // OCR prefill won't silently overwrite the figure.
                                    onClick={() =>
                                      setItems((prev) =>
                                        prev.map((x, j) =>
                                          j === i
                                            ? {
                                                ...x,
                                                taxEdited: true,
                                                vatAmountManual: tax.vatAmount,
                                                whtAmountManual: tax.whtAmount,
                                                dirtyFields: { ...x.dirtyFields, vatAmount: true },
                                              }
                                            : x,
                                        ),
                                      )
                                    }
                                  >
                                    แก้ไข
                                  </button>
                                )}
                              </div>

                              {needsTaxInvoice && (
                                <div
                                  className="badge negative"
                                  data-testid="line-creditable-error"
                                  style={{ fontSize: 12, alignSelf: 'flex-start' }}
                                >
                                  ภาษีซื้อขอคืนได้ ต้องมีใบกำกับภาษีเต็มรูป + เลขผู้เสียภาษีผู้ขาย
                                </div>
                              )}
                              {needsWhtPayee && (
                                <div
                                  className="badge negative"
                                  data-testid="line-wht-payee-error"
                                  style={{ fontSize: 12, alignSelf: 'flex-start' }}
                                >
                                  WHT line ต้องระบุเลขผู้เสียภาษีผู้รับเงิน
                                </div>
                              )}

                              {/* M4 — receipt attachment (upload → MinIO + OCR prefill). */}
                              <div
                                style={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 10,
                                  alignItems: 'center',
                                  fontSize: 13,
                                }}
                              >
                                <label
                                  className="btn sm ghost"
                                  style={{ height: 26, fontSize: 12, cursor: 'pointer' }}
                                >
                                  <Icon name="upload" size={12} /> แนบหลักฐาน
                                  <input
                                    type="file"
                                    data-testid="line-receipt-input"
                                    accept="image/*,application/pdf"
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                      const f = e.target.files?.[0];
                                      if (f) void onUpload(i, f);
                                      e.target.value = '';
                                    }}
                                  />
                                </label>
                                {uploadingLine === i && (
                                  <span className="meta" data-testid="line-receipt-uploading">
                                    กำลังอัปโหลด…
                                  </span>
                                )}
                                {it.attachmentStorageKey ? (
                                  <span
                                    className="badge positive"
                                    data-testid="line-receipt-view"
                                    style={{ fontSize: 12 }}
                                  >
                                    <Icon name="check" size={11} /> ดูไฟล์ — แนบแล้ว
                                  </span>
                                ) : (
                                  <span className="meta">ยังไม่มีไฟล์แนบ</span>
                                )}
                              </div>

                              {needsAttachment && (
                                <div
                                  className="badge negative"
                                  data-testid="line-attachment-error"
                                  style={{ fontSize: 12, alignSelf: 'flex-start' }}
                                >
                                  {it.inputVatDeductible === 'CREDITABLE'
                                    ? 'ภาษีซื้อขอคืนได้ ต้องแนบใบกำกับภาษีเต็มรูป'
                                    : 'รายการ ≥ 500 บาท ต้องแนบหลักฐาน'}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="total">
                    <td colSpan={3}>รวมค่าใช้จ่ายจริง</td>
                    <td className="num tnum">{fmt(total)}</td>
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div
            style={{
              padding: '16px 18px',
              background: diff >= 0 ? '#e7f4ec' : '#fce9e7',
              borderRadius: 8,
              border: `1px solid ${diff >= 0 ? '#bbdebf' : '#f4c1bd'}`,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  background: diff >= 0 ? 'var(--positive)' : 'var(--negative)',
                  color: 'white',
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon name={diff >= 0 ? 'arrowDn' : 'arrowUp'} size={22} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 600 }}>
                  {diff >= 0
                    ? `พนักงานต้องคืนเงินส่วนต่างให้บริษัท ${fmt(returnAmt)} บาท`
                    : `บริษัทต้องจ่ายเพิ่มให้พนักงาน ${fmt(reimbAmt)} บาท`}
                </div>
                <div className="meta" style={{ marginTop: 2 }}>
                  {diff >= 0
                    ? 'รับเงินสดผ่านแคชเชียร์ หรือโอนเข้าบัญชีบริษัท ภายในวันเดียวกัน'
                    : 'จ่ายโอนเข้าบัญชีพนักงาน ภายใน 3 วันทำการ (ยังไม่ลงบัญชีในใบเคลียร์นี้)'}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="meta">{diff >= 0 ? 'ยอดคืน' : 'ยอดจ่ายเพิ่ม'}</div>
                <div
                  style={{
                    fontSize: 24,
                    fontWeight: 700,
                    fontFamily: 'var(--font-display)',
                    color: diff >= 0 ? 'var(--positive)' : 'var(--negative)',
                  }}
                  className="tnum"
                >
                  {fmt(diff >= 0 ? returnAmt : reimbAmt)}
                </div>
              </div>
            </div>
          </div>

          {creditableError && (
            <div
              className="badge negative"
              style={{ fontSize: 13 }}
              data-testid="clear-creditable-error"
            >
              มีรายการภาษีซื้อขอคืนได้ที่ยังไม่ครบเงื่อนไข — ต้องมีใบกำกับภาษีเต็มรูป +
              เลขผู้เสียภาษีผู้ขาย
            </div>
          )}

          {whtPayeeError && (
            <div
              className="badge negative"
              style={{ fontSize: 13 }}
              data-testid="clear-wht-payee-error"
            >
              WHT line ต้องระบุเลขผู้เสียภาษีผู้รับเงิน
            </div>
          )}

          {attachmentError && (
            <div
              className="badge negative"
              style={{ fontSize: 13 }}
              data-testid="clear-attachment-error"
            >
              มีรายการที่ต้องแนบหลักฐาน (≥ 500 บาท หรือ ภาษีซื้อขอคืนได้) แต่ยังไม่ได้แนบไฟล์
            </div>
          )}

          {perDiemError && (
            <div
              className="badge negative"
              style={{ fontSize: 13 }}
              data-testid="clear-perdiem-error"
            >
              มีรายการเบี้ยเลี้ยง/ค่าพาหนะที่ยังไม่ได้เลือกอัตราหรือจำนวน (วัน/กม.) &gt; 0
            </div>
          )}

          {uploadError && (
            <div
              className="badge negative"
              style={{ fontSize: 13 }}
              data-testid="clear-upload-error"
            >
              อัปโหลดหลักฐานล้มเหลว: {uploadError}
            </div>
          )}

          {/* M5 — receipt dedup conflict (409): the server found a duplicate logical
              receipt. Surface an inline override prompt with an audited reason; the
              resubmit sends dupSeq:1 + the reason on the receipt lines. */}
          {dedupConflict ? (
            <div
              className="badge negative"
              data-testid="clear-dedup-error"
              style={{
                fontSize: 13,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                alignItems: 'stretch',
              }}
            >
              <div>
                ใบเสร็จซ้ำ — เอกสารนี้เคยบันทึกแล้ว หากเป็นคนละใบจริง ใส่เหตุผลเพื่อบันทึกซ้ำ
                (override)
              </div>
              <input
                className="input"
                data-testid="dedup-override-reason"
                placeholder="เหตุผลในการบันทึกซ้ำ"
                value={dupReason}
                onChange={(e) => setDupReason(e.target.value)}
                style={{ height: 30, fontSize: 13 }}
              />
              <button
                className="btn sm primary"
                data-testid="clear-dedup-resubmit"
                disabled={!dupReason.trim() || clearAdvance.isPending}
                onClick={() => doSubmit(true)}
                style={{ alignSelf: 'flex-start' }}
              >
                บันทึกซ้ำ (override)
              </button>
            </div>
          ) : (
            clearAdvance.isError && (
              <div className="badge negative" style={{ fontSize: 13 }}>
                บันทึกล้มเหลว: {clearAdvance.error.message}
              </div>
            )
          )}
        </div>

        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 10,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn primary"
              onClick={submit}
              disabled={
                clearAdvance.isPending ||
                items.length === 0 ||
                total <= 0 ||
                creditableError ||
                whtPayeeError ||
                perDiemError ||
                attachmentError
              }
            >
              <Icon name="check" size={14} /> ส่งเคลียร์ให้บัญชี
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
